{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2018 - 2024                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Forms;

{$DEFINE NOPP}

interface

uses
  Classes, Types, SysUtils, WEBLib.Graphics, WEBLib.Controls, WEBLib.Modules,
  WEBLib.Lang, WEBLib.Menus, Web, JS, WEBLib.ClientConnector, Rtl.BrowserLoadHelper
  {$IFDEF SINGLEINSTANCE}
  , WEBLib.WebSocketClient
  {$ENDIF}
  ;

const
  WEBCOREVERSION = '2.5.2.0';

  idOK       = 1;
  idCancel   = 2;
  idAbort    = 3;
  idRetry    = 4;
  idIgnore   = 5;
  idYes      = 6;
  idNo       = 7;
  idClose    = 8;
  idHelp     = 9;
  idTryAgain = 10;
  idContinue = 11;
  mrNone     = 0;
  mrOk       = idOk;
  mrCancel   = idCancel;
  mrAbort    = idAbort;
  mrRetry    = idRetry;
  mrIgnore   = idIgnore;
  mrYes      = idYes;
  mrNo       = idNo;
  mrClose    = idClose;
  mrHelp     = idHelp;
  mrTryAgain = idTryAgain;
  mrContinue = idContinue;
  mrAll      = mrContinue + 1;
  mrNoToAll  = mrAll + 1;
  mrYesToAll = mrNoToAll + 1;

  rgiFirst = -1024;
  rgiLast  = -513;
  igiFirst = 256;
  igiLast  = 511;

  clTMSBlue = $E39E00;
  clTMSWEB = $8F8300;

type
  TDragDockObject = class;
  TCustomForm = class;

  TConstraintSize = 0..MaxInt;
  TCloseAction = (caNone, caHide, caFree, caMinimize);
  TModalResult = Integer;
  TFormStyle = (fsNormal, fsStayOnTop);
  TNavigationTarget = (ntBlank, ntPage);
  TGestureID = rgiFirst..igiLast;
  TInteractiveGestureFlag = (gfBegin, gfInertia, gfEnd);
  TInteractiveGestureFlags = set of TInteractiveGestureFlag;
  TMouseActivate = (maDefault, maActivate, maActivateAndEat, maNoActivate, maNoActivateAndEat);
  TScreenOrientation = (soPortrait, soLandscape);
  TApplicationColorScheme = (csNoScheme, csLight, csDark);

  TSmallPoint  = record
    x, y: SmallInt;
  end;

  TAlignInfo = record
    AlignList: TList;
    ControlIndex: Integer;
    Align: TAlign;
    Scratch: Integer;
  end;

  TGestureEventInfo = record
    GestureID: TGestureID;
    Location: TPoint;
    Flags: TInteractiveGestureFlags;
    Angle: Double;
    InertiaVector: TSmallPoint;
    Distance: Integer;
    TapLocation: TSmallPoint;
  end;

  TOnPaintEvent = TNotifyEvent;
  TCloseEvent = procedure(Sender: TObject; var Action: TCloseAction) of object;
  TCloseQueryEvent = procedure(Sender: TObject; var CanClose: Boolean) of object;
  TOrientationChangeEvent = procedure(Sender: TObject; AOrientation: TScreenOrientation) of object;
  TBeforeUnloadEvent = procedure(Sender: TObject; var AMessage: string) of object;
  TAlignInsertBeforeEvent = function(Sender: TWinControl; C1, C2: TControl): Boolean of object;
  TAlignPositionEvent = procedure(Sender: TWinControl; Control: TControl;
    var NewLeft, NewTop, NewWidth, NewHeight: Integer;
    var AlignRect: TRect; AlignInfo: TAlignInfo) of object;
  TCanResizeEvent = procedure(Sender: TObject; var NewWidth, NewHeight: Integer;
    var Resize: Boolean) of object;
  TConstrainedResizeEvent = procedure(Sender: TObject; var MinWidth, MinHeight,
    MaxWidth, MaxHeight: Integer) of object;
  TContextPopupEvent = procedure(Sender: TObject; MousePos: TPoint; var Handled: Boolean) of object;
  TDockDropEvent = procedure(Sender: TObject; Source: TDragDockObject;
    X, Y: Integer) of object;
  TDockOverEvent = procedure(Sender: TObject; Source: TDragDockObject;
    X, Y: Integer; State: TDragState; var Accept: Boolean) of object;
  TGestureEvent = procedure(Sender: TObject; const EventInfo: TGestureEventInfo;
     var Handled: Boolean) of object;
  TGetSiteInfoEvent = procedure(Sender: TObject; DockClient: TControl;
    var InfluenceRect: TRect; MousePos: TPoint; var CanDock: Boolean) of object;
  TMouseActivateEvent = procedure(Sender: TObject; Button: TMouseButton;
    Shift: TShiftState; X, Y: Integer; HitTest: Integer; var MouseActivate: TMouseActivate) of object;
  TMouseWheelUpDownEvent = procedure(Sender: TObject; Shift: TShiftState;
    MousePos: TPoint; var Handled: Boolean) of object;
  TStartDockEvent = procedure(Sender: TObject;
    var DragObject: TDragDockObject) of object;
  TUnDockEvent = procedure(Sender: TObject; Client: TControl;
    NewTarget: TWinControl; var Allow: Boolean) of object;

  THashChangeEvent = procedure(Sender: TObject; oldURL, newURL: string) of object;

  TModalResultProc = reference to procedure(AValue: TModalResult);
  TFormCreatedProc = reference to procedure(AForm: TObject);

  TWindowState = (wsNormal, wsMinimized, wsMaximized);

  TPopupMode = (pmNone, pmAuto, pmExplicit);
  TPopupClose = (pcOnDeactivate, pcNever);
  TPosition = (poDesigned, poDefault, poDefaultPosOnly, poDefaultSizeOnly, poScreenCenter, poDesktopCenter, poMainFormCenter, poOwnerFormCenter);

  TApplicationErrorType = (aeSilent, aeDialog, aeAlert, aeFooter);

  TFormBorderStyle = (fbNone, fbSingle, fbSizeable, fbDialog, fbDialogSizeable);
  TFormBorder = fbNone..fbSingle;

  TBootstrapVersion = (bv3, bv4, bv5);

  TCSSLibrary = (cssNone, cssBootstrap);

  TCustomSizeConstraints = class(TPersistent)
  private
    FControl: TControl;
    FMaxHeight: TConstraintSize;
    FMaxWidth: TConstraintSize;
    FMinHeight: TConstraintSize;
    FMinWidth: TConstraintSize;
    FOnChange: TNotifyEvent;
    procedure SetConstraints(AIndex: Integer; AValue: TConstraintSize);
  protected
    procedure Change; virtual;
    procedure AssignTo(ADest: TPersistent); override;
    property Control: TControl read FControl;
    property MaxHeight: TConstraintSize index 0 read FMaxHeight write SetConstraints default 0;
    property MaxWidth: TConstraintSize index 1 read FMaxWidth write SetConstraints default 0;
    property MinHeight: TConstraintSize index 2 read FMinHeight write SetConstraints default 0;
    property MinWidth: TConstraintSize index 3 read FMinWidth write SetConstraints default 0;
  public
    constructor Create(AControl: TControl); virtual;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TSizeConstraints = class(TCustomSizeConstraints)
  published
    property MaxHeight;
    property MaxWidth;
    property MinHeight;
    property MinWidth;
  end;

  TDragDockObject = class(TObject)
  strict private
    FBrush: TBrush;
    FDockRect: TRect;
    FDropAlign: TAlign;
    FDropOnControl: TControl;
    FEraseDockRect: TRect;
    FEraseWhenMoving: Boolean;
    FFloating: Boolean;
    FFrameWidth: Integer;
  public
    constructor Create(AControl: TControl);
    destructor Destroy; override;
    procedure Assign(Source: TDragObject);
    property Brush: TBrush read FBrush write FBrush;
    property DockRect: TRect read FDockRect write FDockRect;
    property DropAlign: TAlign read FDropAlign;
    property DropOnControl: TControl read FDropOnControl;
    property EraseDockRect: TRect read FEraseDockRect write FEraseDockRect;
    property EraseWhenMoving: Boolean read FEraseWhenMoving;
    property Floating: Boolean read FFloating write FFloating;
    property FrameWidth: Integer read FFrameWidth;
  end;

  TCustomForm = class(TWinControl)
  private
    FDesignContainer: TJSElement;
    FLayer: TJSElement;
    FPopup: Boolean;
    FPopupElement: TJSHTMLElement;
    FFormFileName: string;
    FFormContent: string;
    FFormContainer: TElementID;
    FFormElement: string;
    FFormStyle: TFormStyle;
    FOnCreate: TNotifyEvent;
    FOnShow: TNotifyEvent;
    FModalResult: TModalResult;
    FOnPaint: TOnPaintEvent;
    FOnDeactivate: TNotifyEvent;
    FOnCloseQuery: TCloseQueryEvent;
    FOnClose: TCloseEvent;
    FModalProc: TModalResultProc;
    FCreatedProc: TFormCreatedProc;
    FWindowState: TWindowState;
    FCaption: string;
    FIsResizing: boolean;
    FOnScroll: TNotifyEvent;
    FOnUnload: TNotifyEvent;
    FOnBeforeUnload: TBeforeUnloadEvent;
    FPopupMode: TPopupMode;
    FPopupClose: TPopupClose;
    FPosition: TPosition;
    FActiveControl: TWinControl;
    FMdx, FMdy: integer;
    FDlgX, FDlgY: integer;
    FCaptured: boolean;
    FDown: boolean;
    FHasCaption: boolean;
    FMoveSpan: TJSHTMLElement;
    FCaptionElement: TJSHTMLElement;
    FCaptionCloseElement: TJSHTMLElement;
    FBorder: TFormBorderStyle;
    FShadow: boolean;
    FTimerID: integer;
    FOrigWidth, FOrigHeight: integer;
    FOldWidth, FOldHeight: integer;
    FOnDestroy: TNotifyEvent;
    FPopupOpacity: single;
    FCreating: boolean;
    FShown: boolean;
    FLoadedPtr: pointer;
    FDOMContentLoadedPtr: pointer;
    FUnloadPtr: pointer;
    FBeforeUnloadPtr: pointer;
    FResizePtr: pointer;
    FScrollPtr: pointer;
    FDocMouseUpPtr: pointer;
    FDocMouseMovePtr: pointer;
    FTitleDownPtr: pointer;
    FDoClickPtr: pointer;
    FKeyDownPtr: pointer;
    FOrientationChangePtr: pointer;
    FHashChangePtr: pointer;
    FCSSLibrary: TCSSLibrary;
    FNoHTML: boolean;
    FPrevActiveForm: TCustomForm;
    FElementCaptionClassName: TElementClassName;
    FElementPopupClassName: TElementClassName;
    FMenu: TCustomControl;
    FResizeObserver: TJSObject;
    FOnOrientationChange: TOrientationChangeEvent;
    FOnDOMContentLoaded: TNotifyEvent;
    FShowClose: boolean;
    FOnHashChange: THashChangeEvent;
    procedure SetModalResult(const Value: TModalResult);
    function GetFormStyle: TFormStyle;
    procedure SetFormStyle(const Value: TFormStyle); virtual;
    function GetCanvas: TCanvas;
    procedure SetCaption(const AValue: string); virtual;
    procedure SetActiveControl(const Value: TWinControl);
    procedure SetShadow(const Value: boolean);
    procedure SetBorder(const Value: TFormBorderStyle);
    procedure SetFormContainer(const Value: TElementID);
    function GetActiveControl: TWinControl;
    procedure SetWidth(AValue: Integer); override;
    procedure SetHeight(AValue: Integer); override;
    procedure SetPosition(const Value: TPosition); virtual;
    procedure SetClientHeight(const Value: Integer); override;
    function GetClientHeight: integer; override;
    procedure ReadPixelsPerInch(AReader: TReader);
    procedure WritePixelsPerInch(AWriter: TWriter);
  protected
    function HandleHashChange(Event: TEventListenerEvent): boolean; virtual;
    function HandleLoaded(Event: TEventListenerEvent): boolean; virtual;
    function HandleDOMContentLoaded(Event: TEventListenerEvent): boolean; virtual;
    function HandleScroll(Event: TEventListenerEvent): boolean; virtual;
    function HandleUnload(Event: TEventListenerEvent): boolean; virtual;
    function HandleBeforeUnload(Event: TEventListenerEvent): boolean; virtual;
    function HandleObserver: boolean; virtual;
    function HandleResize(Event: TEventListenerEvent): boolean; virtual;
    function HandleDocMouseMove(Event: TJSMouseEvent): Boolean; virtual;
    function HandleDocMouseUp(Event: TJSMouseEvent): Boolean; virtual;
    function HandleTitleDown(Event: TJSMouseEvent): Boolean; virtual;
    function HandleCloseClick(Event: TEventListenerEvent): boolean; virtual;

    procedure SetLeft(AValue: Integer); override;
    procedure SetTop(AValue: Integer); override;

    procedure DefineProperties(AFiler: TFiler); override;
    procedure ClearMethodPointers; override;
    procedure GetMethodPointers; override;
    procedure VisibleChanged; override;
    procedure Resize; reintroduce; virtual;
    procedure CreateControl; override;
    procedure DoClose(var CloseAction: TCloseAction); virtual;
    procedure BindEvents; override;
    procedure UnbindEvents; override;
    procedure DoCreate; virtual;
    procedure Realign; override;
    procedure DoResize; virtual;
    procedure DoShow; virtual;
    procedure DoAutoFocus; virtual;
    procedure Paint; virtual;
    function HandleDoClick(Event: TJSMouseEvent): Boolean; reintroduce; virtual;
    function HandleKeyDown(Event: TJSKeyBoardEvent): Boolean; virtual;
    function HandleOrientationChange(Event: TJSEvent): Boolean; virtual;
    procedure HandleDoResize; virtual;
    procedure KeyPreview(var Key: Word; Shift: TShiftState); override;
    function GetWidth: Integer; override;
    function GetHeight: Integer; override;
    function GetLeft: Integer; override;
    function GetTop: Integer; override;
    function GetClientRect: TRect; override;
    function CreateElement: TJSElement; override;
    procedure UpdateElement; override;
    function ContainerElement: TJSElement; override;
    function FormContainerElement: TJSElement; virtual;
    function GetElementBindHandle: TJSEventTarget; override;
    function GetElementHandle: TJSHTMLElement; override;
    procedure SetElementClassName(AValue: string); override;
    function CloseQuery: Boolean; virtual;
    procedure LoadDFMValues; virtual;
    procedure Init; virtual;
    property FormContent: string read FFormContent write FFormContent;
    property WindowState: TWindowState read FWindowState write FWindowState;
    function IsFocused: Boolean; override;
    property PopupMode: TPopupMode read FPopupMode write FPopupMode default pmNone;
    property Position: TPosition read FPosition write SetPosition default poDefaultPosOnly;
    function CreateLayer: TJSHTMLElement;
    function CreateCaption: TJSHTMLElement;
    function GetCaptionHeight: integer;
    procedure SetColorScheme(AScheme: TApplicationColorScheme); virtual;
    procedure SetFormPos; virtual;
    property Border: TFormBorderStyle read FBorder write SetBorder;
    class function GetHTMLFileName: string; virtual;
    procedure LoadedDone; virtual;
    procedure Loaded; override;
    procedure ColorChanging; override;
  public
    procedure CreateInitialize; override;
    constructor Create(id: string); overload; override;
    constructor Create(id: string; var AReference); overload; virtual;
    constructor Create(AOwner: TComponent); overload; override;
    constructor Create(AOwner: TComponent; IsPopup: boolean; ABorder: TFormBorderStyle = fbSingle; ATitle: string = ''); overload;
    constructor CreateNew(AOwner: TComponent; Dummy: Integer = 0); overload; virtual;
    constructor CreateNew(AFileName: string); overload; virtual;
    constructor CreateNew; overload; virtual;
    constructor CreateNew(AProc: TFormCreatedProc); overload; virtual;
    constructor CreateNew(AElementID: string; AProc: TFormCreatedProc); overload; virtual;
    constructor CreateNew(AElementID, AFileName: string; AProc: TFormCreatedProc); overload; virtual;
    constructor CreateDialog(ACaption: string); virtual;
    constructor CreateDialogNew(ACaption: string); virtual;

    [async]
    function Load: TJSPromise;
    [async]
    function Execute: TJSPromise;
    [async]
    function LoadAndExecute: TJSPromise;

    destructor Destroy; override;
    procedure Close; virtual;
    function GetUniqueComponentName(AComponent: TComponent): string;
    function IsMiletus: boolean;
    procedure PreventDefault; reintroduce;
    property ActiveControl: TWinControl read GetActiveControl write SetActiveControl;
    property ModalResult: TModalResult read FModalResult write SetModalResult;
    property Canvas: TCanvas read GetCanvas;
    property CaptionElement: TJSHTMLElement read FCaptionElement;
    property ElementCaptionClassName: TElementClassName read FElementCaptionClassName write FElementCaptionClassName;
    property ElementPopupClassName: TElementClassName read FElementPopupClassName write FElementPopupClassName;
    procedure SetBounds(X, Y, AWidth, AHeight: Integer); override;
    procedure SelectFirst;
    procedure SelectNext(CurControl: TControl; GoForward: boolean; CheckTabStop: boolean);
    procedure Show; reintroduce;
    function ShowModal: Integer; overload; virtual;
    function ShowModal(AProc: TModalResultProc): TModalResult; overload; virtual;
    procedure AddCSS(const id: string; const css: string);
    procedure RemoveCSS(const id: string);
    procedure UpdateCSS(const id: string; const css: string);
    property Caption: string read FCaption write SetCaption;
    property Color;
    property CSSLibrary: TCSSLibrary read FCSSLibrary write FCSSLibrary default cssNone;
    property FormContainer: TElementID read FFormContainer write SetFormContainer;
    property FormFileName: string read FFormFileName write FFormFileName;
    property FormStyle: TFormStyle read GetFormStyle write SetFormStyle;
    property IsResizing: boolean read FIsResizing;
    property Menu: TCustomControl read FMenu write FMenu;
    property Popup: boolean read FPopup write FPopup;
    property PopupClose: TPopupClose read FPopupClose write FPopupClose default pcOnDeactivate;
    property PopupOpacity: single read FPopupOpacity write FPopupOpacity;
    property ShowClose: boolean read FShowClose write FShowClose default true;
    property Shadow: boolean read FShadow write SetShadow default true;
    property OnBeforeUnload: TBeforeUnloadEvent read FOnBeforeUnload write FOnBeforeUnload;
    property OnClick;
    property OnDblClick;
    property OnDOMContentLoaded: TNotifyEvent read FOnDOMContentLoaded write FOnDOMContentLoaded;
    property OnClose: TCloseEvent read FOnClose write FOnClose;
    property OnCloseQuery: TCloseQueryEvent read FOnCloseQuery write FOnCloseQuery;
    property OnCreate: TNotifyEvent read FOnCreate write FOnCreate;
    property OnDestroy: TNotifyEvent read FOnDestroy write FOnDestroy;
    property OnDeactivate: TNotifyEvent read FOnDeactivate write FOnDeactivate;
    property OnOrientationChange: TOrientationChangeEvent read FOnOrientationChange write FOnOrientationChange;
    property OnPaint: TOnPaintEvent read FOnPaint write FOnPaint;
    property OnScroll: TNotifyEvent read FOnScroll write FOnScroll;
    property OnShow: TNotifyEvent read FOnShow write FOnShow;
    property OnUnload: TNotifyEvent read FOnUnload write FOnUnload;
    property OnHashChange: THashChangeEvent read FOnHashChange write FOnHashChange;
  end;

  TFormClass = class of TForm;
  TForm = class(TCustomForm)
  public
    property Border;
    property ClientHeight;
    property ClientWidth;
    property PopupMode;
    property Position;
  published
    property Align;
    property Caption;
    property Color;
    property CSSLibrary;
    property ElementClassName;
    property ElementFont;
    property ElementPosition;
    property Font;
    property FormContainer;
    property FormStyle;
    property Menu;
    property ParentFont;
    property Shadow;
    property ShowClose;
    property TabOrder;
    property TabStop;
    property OnBeforeUnload;
    property OnClick;
    property OnClose;
    property OnCloseQuery;
    property OnCreate;
    property OnDblClick;
    property OnDeactivate;
    property OnDestroy;
    property OnDOMContentLoaded;
    property OnHashChange;
    property OnResize;
    property OnShow;
    property OnScroll;
    property OnOrientationChange;
    property OnPaint;
    property OnTouchStart;
    property OnTouchMove;
    property OnTouchEnd;
    property OnUnload;
  end;

  TWebForm = TForm;

  //TTouchManager = class(TComponent)
  //end;

  TCustomFrame = class(TCustomHTMLDiv)
  private
    FAutoScroll: Boolean;
    FAutoSize: Boolean;
    FBiDiMode: TBiDiMode;
    FConstraints: TSizeConstraints;
    FCtl3D: Boolean;
    FDockSite: Boolean;
    FDragCursor: TCursor;
    FDragKind: TDragKind;
    FDragMode: TDragMode;
    FOnAlignInsertBefore: TAlignInsertBeforeEvent;
    FOnAlignPosition: TAlignPositionEvent;
    FOnCanResize: TCanResizeEvent;
    FOnConstrainedResize: TConstrainedResizeEvent;
    FOnContextPopup: TContextPopupEvent;
    FOnDockDrop: TDockDropEvent;
    FOnDockOver: TDockOverEvent;
    FOnEndDock: TEndDragEvent;
    FOnGesture: TGestureEvent;
    FOnGetSiteInfo: TGetSiteInfoEvent;
    FOnMouseActivate: TMouseActivateEvent;
    FOnMouseWheelDown: TMouseWheelUpDownEvent;
    FOnMouseWheelUp: TMouseWheelUpDownEvent;
    FOnStartDock: TStartDockEvent;
    FOnUnDock: TUnDockEvent;
    FPadding: TPadding;
    FParentBackground: Boolean;
    FParentCtl3D: Boolean;
    FPopupMenu: TPopupMenu;
    procedure LoadHTMLFile(const AId: string);
    //FTouch: TTouchManager;
    //FTouchManager: TTouchManager;
    procedure SetConstraints(const AValue: TSizeConstraints);
    procedure SetPopupMenu(AValue: TPopupMenu);
    procedure SetPadding(AValue: TPadding);
    //procedure SetTouchManager(AValue: TTouchManager);
  protected
    class function GetHTMLFileName: string; virtual;
    procedure LoadDFMValues; virtual;
    procedure UpdateElement; override;
    property AutoScroll: Boolean read FAutoScroll write FAutoScroll default False;
    property AutoSize: Boolean read FAutoSize write FAutoSize default False;
    property BiDiMode: TBiDiMode read FBiDiMode write FBiDiMode stored False;
    property Constraints: TSizeConstraints read FConstraints write SetConstraints;
    property Ctl3D: Boolean read FCtl3D write FCtl3D stored False;
    property DockSite: Boolean read FDockSite write FDockSite default False;
    property DragCursor: TCursor read FDragCursor write FDragCursor default crDrag;
    property DragKind: TDragKind read FDragKind write FDragKind default dkDrag;
    property DragMode: TDragMode read FDragMode write FDragMode default dmManual;
    property Padding: TPadding read FPadding write SetPadding;
    property ParentBackground: Boolean read FParentBackground write FParentBackground;
    property ParentCtl3D: Boolean read FParentCtl3D write FParentCtl3D default True;
    property PopupMenu: TPopupMenu read FPopupMenu write SetPopupMenu;
    //property Touch: TTouchManager read FTouch write SetTouchManager;
    property OnAlignInsertBefore: TAlignInsertBeforeEvent read FOnAlignInsertBefore write FOnAlignInsertBefore;
    property OnAlignPosition: TAlignPositionEvent read FOnAlignPosition write FOnAlignPosition;
    property OnCanResize: TCanResizeEvent read FOnCanResize write FOnCanResize;
    property OnConstrainedResize: TConstrainedResizeEvent read FOnConstrainedResize write FOnConstrainedResize;
    property OnContextPopup: TContextPopupEvent read FOnContextPopup write FOnContextPopup;
    property OnDockDrop: TDockDropEvent read FOnDockDrop write FOnDockDrop;
    property OnDockOver: TDockOverEvent read FOnDockOver write FOnDockOver;
    property OnEndDock: TEndDragEvent read FOnEndDock write FOnEndDock;
    property OnGesture: TGestureEvent read FOnGesture write FOnGesture;
    property OnGetSiteInfo: TGetSiteInfoEvent read FOnGetSiteInfo write FOnGetSiteInfo;
    property OnMouseActivate: TMouseActivateEvent read FOnMouseActivate write FOnMouseActivate;
    property OnMouseWheelDown: TMouseWheelUpDownEvent read FOnMouseWheelDown write FOnMouseWheelDown;
    property OnMouseWheelUp: TMouseWheelUpDownEvent read FOnMouseWheelUp write FOnMouseWheelUp;
    property OnStartDock: TStartDockEvent read FOnStartDock write FOnStartDock;
    property OnUnDock: TUnDockEvent read FOnUnDock write FOnUnDock;
  public
    constructor Create(AId: string); override; overload;
    constructor Create(AOwner: TComponent); override; overload;
    constructor CreateNew(AOwner: TComponent);
    destructor Destroy; override;
    procedure LoadFromForm;
    property ElementHandle;
  end;

  TFrameClass = class of TFrame;
  TFrame = class(TCustomFrame)
  published
    property Align;
    property Anchors;
    property AutoScroll;
    property AutoSize;
    property BiDiMode;
    property Constraints;
    property DockSite;
    property DoubleBuffered;
    property DragCursor;
    property DragKind;
    property DragMode;
    property Enabled;
    property Color nodefault;
    property Ctl3D;
    property Font;
    property Padding;
    property ParentBackground default True;
    property ParentBiDiMode;
    property ParentColor;
    property ParentCtl3D;
    property ParentDoubleBuffered;
    property ParentFont;
    property ParentShowHint;
    property PopupMenu;
    property ShowHint;
    property TabOrder;
    property TabStop;
    //property Touch;
    property Visible;
    property OnAlignInsertBefore;
    property OnAlignPosition;
    property OnCanResize;
    property OnClick;
    property OnConstrainedResize;
    property OnContextPopup;
    property OnDblClick;
    property OnDockDrop;
    property OnDockOver;
    property OnDragDrop;
    property OnDragOver;
    property OnEndDock;
    property OnEndDrag;
    property OnEnter;
    property OnExit;
    property OnGesture;
    property OnGetSiteInfo;
    property OnMouseActivate;
    property OnMouseDown;
    property OnMouseEnter;
    property OnMouseLeave;
    property OnMouseMove;
    property OnMouseUp;
    property OnMouseWheel;
    property OnMouseWheelDown;
    property OnMouseWheelUp;
    property OnResize;
    property OnStartDock;
    property OnStartDrag;
    property OnUnDock;
  end;
  TWebFrame = TFrame;

  TApplicationError = record
    AMessage: string;
    AFile: string;
    ALineNumber: integer;
    AColNumber: integer;
    AStack: string;
    AError: TJSObject;
  end;

  THintPosition = (hpLeft, hpTop, hpRight, hpBottom);

  TOnlineStatus = (osOnline, osOffline);

  TApplicationErrorEvent = procedure(Sender: TObject; AError: TApplicationError; var Handled: boolean) of object;

  TApplicationHashChangeEvent = procedure(Sender: TObject; AHash: string; var Handled: boolean) of object;

  TApplicationOnlineChangeEvent = procedure(Sender: TObject; AStatus: TOnlineStatus) of object;

  TApplicationActiveChangeEvent = procedure(Sender: TObject; Active: boolean) of object;

  TApplicationCallBackEvent = procedure(Sender: TObject; AQuery: string) of object;

  TApplicationExceptionEvent = procedure(Sender: TObject; E: Exception) of object;

  TApplicationPopStateEvent = procedure(Sender: TObject; State: JSValue) of object;

  TApplicationAutoCreateFormEvent = procedure(Sender: TObject; AInstanceClass: TFormClass; var AReference) of object;

  TApplication = class(TControl)
  private
    FAppContainer: TElementID;
    FMainFormOnTaskBar: Boolean;
    FCanCreateForm: Boolean;
    FAppInitializing: Boolean;
    FLastReq: TJSXMLHttpRequest;
    FFormCaptionHeight: integer;
    {$IFDEF SINGLEINSTANCE}
    FWebSocket: TSocketClient;
    {$ENDIF}
    FActiveForm: TCustomForm;
    FMainForm: TCustomForm;
    FLoadForm: TCustomForm;
    FInitFormClassName: string;
    FFormStack: TList;
    FParameters: TStrings;
    FIsRedirect: boolean;
    FLanguage: TUILanguage;
    FOnImageCacheReady: TNotifyEvent;
    FOnError: TApplicationErrorEvent;
    FOnHashChange: TApplicationHashChangeEvent;
    FAutoFormRoute: boolean;
    FErrorType: TApplicationErrorType;
    FClientConnector: TClientConnector;
    FOnOnlineChange: TApplicationOnlineChangeEvent;
    FOnActivate: TApplicationActiveChangeEvent;
    FThemeTextColor: TColor;
    FThemeColor: TColor;
    FThemed: boolean;
    FThemeButtonClassName: string;
    FOnOAuthToken: TApplicationCallBackEvent;
    FOnOAuthCallBack: TApplicationCallBackEvent;
    FOnFontCacheReady: TNotifyEvent;
    FOnPopState: TApplicationPopStateEvent;
    FMaxZIndex: integer;
    FCreatedProc: TFormCreatedProc;
    FHandleOAuth: boolean;
    FHintPause: integer;
    FHintHidePause: integer;
    FHintPosition: THintPosition;
    FHintClassName: string;
    FOnExit: TNotifyEvent;
    FOnException: TApplicationExceptionEvent;
    FOnAutoCreateForm: TApplicationAutoCreateFormEvent;
    FRouting: boolean;
    {$IFDEF FREEWARE}
    FNotifyCount: integer;
    {$ENDIF}
    {$IFDEF HOTRELOAD}
    FHotReloadPollInterval: Integer;
    FHotReloadReq: TJSXMLHttpRequest;
    FHotReloadTimer: NativeInt;
    FHotReloadVersion: Integer;
    FHotReloadFile: string;
    {$ENDIF}
    function DoFormLoad(Event: TEventListenerEvent): boolean;
    function DoFormAbort(Event: TEventListenerEvent): boolean;
    function DoHandleError(Event: TJSErrorEvent): boolean;
    function DoHandleExit(Event: TEventListenerEvent): boolean;
    function DoHashChange(Event: TEventListenerEvent): boolean;
    function DoPopState(Event: TJSPopStateEvent): boolean;
    function DoErrorClose(Event: TJSMOuseEvent): boolean;
    function DoUpdateOnlineStatus(Event: TEventListenerEvent): boolean;
    function DoVisibilityChange(Event: TEventListenerEvent): boolean;
    procedure SetLanguage(const Value: TUILanguage);
    function GetIsOnline: boolean;
    function GetIsMobile: boolean;
    function GetIsPWA: boolean;
    procedure InternalDownloadFile(const AData: TJSUint8Array; const AFileName, AType: string; const ANewTab: Boolean);
    {$IFDEF SINGLEINSTANCE}
    procedure DoGetDataReceived(Sender: TObject; Origin: string; Data: TJSObjectRecord);
    procedure DoGetConnected(Sender: TObject);
    {$ENDIF}
    {$IFDEF HOTRELOAD}
    procedure OnHotReloadTick;
    function OnHotReloadLoad(AEvent: TEventListenerEvent): Boolean;
    function OnHotReloadAbort(AEvent: TEventListenerEvent): Boolean;
    procedure HandleHotReload(const AData: TJSObject);
    procedure StartHotReload;
    procedure StopHotReload;
    {$ENDIF}
  protected
    procedure CreateNewForm(AForm: TCustomForm; HTML: string); overload; virtual;
    function GetFormExtension: string;
    function GetAuthorizationPageHTML(const AAuthorizationSuccess: Boolean): string;
    function GetColorScheme: TApplicationColorScheme; virtual;
    function GetActive: boolean;
    function GetBootstrapVersion: TBootstrapVersion;
    procedure ReloadForm;
    procedure ActivateChildScripts(AElement: TJSHTMLElement);
    procedure ChangeMaxZIndex(Delta: integer);
    procedure TriggerNotification;
    procedure AutoCreateForm(AInstanceClass: TFormClass; var AReference);
    procedure SetHash(const AValue: string);
    function GetHash: string;
  public
    procedure PushForm(AForm: TCustomForm);
    function PopForm: TCustomForm;
    procedure LockForm(AForm: TCustomForm);
    procedure UnLockForm(AForm: TCustomForm);
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    property MaxZIndex: integer read FMaxZIndex write FMaxZIndex;
    function MaxZIndexStr: string;
    function IsPWAInstalled: TJSPromise;
    procedure GoBack;
    function CreateNewForm(AInstanceClass: TFormClass): TCustomForm; overload;
    function CreateNewForm(AInstanceClass: TFormClass; AElementID: string): TCustomForm; overload;
    procedure CreateForm(AInstanceClass: TFormClass; var AReference); overload;
    procedure CreateForm(AInstanceClass: TFormClass; AElementID: string; var AReference); overload;
    procedure CreateForm(AInstanceClass: TFormClass; AElementID: string; var AReference; AProc: TFormCreatedProc); overload;
    procedure CreateForm(AInstanceClass: TFormClass; AElementID: string; var AReference; AProc: TFormCreatedProc; LoadHTML: boolean); overload;
    procedure CreateForm(AInstanceClass: TDataModuleClass; var AReference); overload;
    procedure CreateForm(AInstanceClass: TFrameClass; var AReference); overload;
    procedure CreateForm(AInstanceClass: TFormClass; AElement: TJSHTMLElement; var AReference); overload;
    procedure CreateFormDirect(AInstanceClass: TFormClass; var AReference); overload;
    procedure CreateFormDirect(AInstanceClass: TFormClass; AElementID: string; var AReference); overload;
    procedure CreateFormDirect(AInstanceClass: TDataModuleClass; var AReference); overload;
    procedure CreateFormDirect(AInstanceClass: TFrameClass; var AReference); overload;
    procedure LoadForm(AForm: TCustomForm; AFormFile: string); overload; virtual;
    procedure LoadForm(AForm: TCustomForm; AFormFile: string; FormLoaded: TFormCreatedProc); overload; virtual;
    procedure ChangeCSS(id: string; href:string);
    procedure InsertCSS(id, href: string);
    procedure RemoveCSS(id: string);
    function HasCSS(href:string): boolean;
    procedure ChangeLanguage(const Value: TUILanguage); virtual;
    procedure Initialize;
    function Route: boolean;
    procedure InitFormatSettings(const BrowserLocale: string);
    procedure ReceiveMessageFromClient(const AMessage: string);
    procedure RouteForm(AParameter: string);
    procedure Run;
    procedure RunScript(Source: string);
    procedure Terminate;
    procedure Navigate(const AURL: string; ATarget: TNavigationTarget = ntBlank);
    procedure Download(const AURL: string);
    procedure DownloadTextFile(const AText: string; AFileName: string = '');
    procedure DownloadBinaryFile(const AData: TJSBlob; const AFileName: string = ''; const ANewTab: Boolean = False); overload;
    procedure DownloadBinaryFile(const AData: TBytes; const AFileName: string = ''; const ANewTab: Boolean = False); overload;
    procedure DownloadBinaryFile(const AData: TJSUint8Array; const AFileName: string = ''; const ANewTab: Boolean = False); overload;
    procedure DownloadPDFFile(const AData: TBytes; const AFileName: string = ''; const ANewTab: Boolean = False); overload;
    procedure DownloadPDFFile(const AData: TJSUint8Array; const AFileName: string = ''; const ANewTab: Boolean = False); overload;
    function EXEName: string;
    function IDETheme: string;
    function IDECSS: string;
    function Version: string;
    procedure IDEMessage(const Msg: string);
    procedure IDEOpenURL(const URL: string);
    function NeedsFormRouting: Boolean;
    function ObjectURL(AFile: TJSHTMLFile): string;
    property Active: boolean read GetActive;
    property AppContainer: TElementID read FAppContainer write FAppContainer;
    property AutoFormRoute: boolean read FAutoFormRoute write FAutoFormRoute;
    property ErrorType: TApplicationErrorType read FErrorType write FErrorType;
    property BootstrapVersion: TBootstrapVersion read GetBootstrapVersion;
    property FormCaptionHeight: integer read FFormCaptionHeight write FFormCaptionHeight;
    property Hash: string read GetHash write SetHash;
    property HandleOAuth: boolean read FHandleOAuth write FHandleOAuth;
    property HintHidePause: integer read FHintHidePause write FHintHidePause;
    property HintPause: integer read FHintPause write FHintPause;
    property HintClassName: string read FHintCLassName write FHintClassName;
    property HintPosition: THintPosition read FHintPosition write FHintPosition;
    property IsOnline: boolean read GetIsOnline;
    property IsMobile: boolean read GetIsMobile;
    property IsPWA: boolean read GetIsPWA;
    property ActiveForm: TCustomForm read FActiveForm;
    property ColorScheme: TApplicationColorScheme read GetColorScheme;
    property Routing: boolean read FRouting write FRouting;
    property MainForm: TCustomForm read FMainForm;
    property MainFormOnTaskBar: boolean read FMainFormOnTaskBar write FMainFormOnTaskBar;
    property Parameters: TStrings read FParameters;
    property Language: TUILanguage read FLanguage write SetLanguage;
    property Themed: boolean read FThemed write FThemed;
    property ThemeColor: TColor read FThemeColor write FThemeColor;
    property ThemeTextColor: TColor read FThemeTextColor write FThemeTextColor;
    property ThemeButtonClassName: string read FThemeButtonClassName write FThemeButtonClassName;
    property OnError: TApplicationErrorEvent read FOnError write FOnError;
    property OnException: TApplicationExceptionEvent read FOnException write FOnException;
    property OnExit: TNotifyEvent read FOnExit write FOnExit;
    property OnAutoCreateForm: TApplicationAutoCreateFormEvent read FOnAutoCreateForm write FOnAutoCreateForm;
    property OnImageCacheReady: TNotifyEvent read FOnImageCacheReady write FOnImageCacheReady;
    property OnFontCacheReady: TNotifyEvent read FOnFontCacheReady write FOnFontCacheReady;
    property OnHashChange: TApplicationHashChangeEvent read FOnHashChange write FOnHashChange;
    property OnOnlineChange: TApplicationOnlineChangeEvent read FOnOnlineChange write FOnOnlineChange;
    property OnOAuthCallBack: TApplicationCallBackEvent read FOnOAuthCallBack write FOnOAuthCallBack;
    property OnOAuthToken: TApplicationCallBackEvent read FOnOAuthToken write FOnOAuthToken;
    property OnActivate: TApplicationActiveChangeEvent read FOnActivate write FOnActivate;
    property OnPopState: TApplicationPopStateEvent read FOnPopState write FOnPopState;
    property ClientConnector: TClientConnector read FClientConnector write FClientConnector;
{$IFDEF HOTRELOAD}
    property HotReloadPollInterval: Integer read FHotReloadPollInterval write FHotReloadPollInterval;
    property HotReloadFile: string read FHotReloadFile write FHotReloadFile;
{$ENDIF}
  end;

function GetParentForm(AControl: TControl): TCustomForm;
procedure Log(v: JSValue); overload;
procedure Log(arr: array of JSValue); overload;
function WinWidth: integer;
function WinHeight: integer;
function Sleep(milliseconds: cardinal): TJSPromise;

var
  Application: TApplication;
  HandShakeScript: TJSHTMLScriptElement;
  VSIDE: TJSObject;
  LastHash: string;

implementation

uses
  WEBLib.Dialogs, WEBLib.WebTools, WEBLib.JSON, WEBLib.Utils, Math
  {$IFDEF HOTRELOAD}, IOUtils{$ENDIF};

type
  TFramePreviewForm = class(TWebForm)
  private
    FFrame: TFrame;
  protected
    procedure LoadDFMValues; override;
  end;

procedure Log(v: JSValue);
begin
  asm
    console.log(v);
  end;
end;

function WinHeight: integer;
var
  h: integer;
begin
  if Assigned(VSIDE) then
  begin
     asm
       h = pas["WEBLib.Forms"].VSIDE.clientHeight();
     end;
  end
  else
    h := window.innerHeight;

  Result := h;
end;

function WinWidth: integer;
var
  w: integer;
begin
  if Assigned(VSIDE) then
  begin
    asm
      w = pas["WEBLib.Forms"].VSIDE.clientWidth();
    end;
  end
  else
    w := window.innerWidth;

  Result := w;
end;


{$HINTS OFF}
procedure Log(arr: array of JSValue);
var
  i: integer;
  s,su: string;
begin
  s := '[';
  for i := 0 to length(arr) - 1 do
  begin
    asm
      function isPrimitive(test) {
        return (test !== Object(test));
      };

      if (isPrimitive(arr[i])) {
         var su = arr[i].toString();
         if (s != "[") { s = s + ","; }
         s = s + su;
         }
      else
      {
        console.log(arr[i]);
         if (s != "[") { s = s + ","; }
         s = s + "#object";
      }
    end;
  end;

  s := s + ']';

  log(s);
end;
{$HINTS ON}

function GetParentForm(AControl: TControl): TCustomForm;
var
  FOwner: TComponent;
begin
  Result := nil;

  FOwner := AControl;

  while Assigned(FOwner) and not (FOwner is TCustomForm) do
  begin
    // it is a control without Owner, created with HTML element ID
    if (FOwner = AControl) and (FOwner.Owner = nil) then
      FOwner := AControl.Parent
    else
      FOwner := FOwner.Owner;
  end;

  if Assigned(FOwner) and (FOwner is TCustomForm) then
    Result := (FOwner as TCustomForm);
end;


{ TCustomForm }

function TCustomForm.Load: TJSPromise;
begin
  FShown := false;
  FormFileName := GetHTMLFileName;

  if FormFileName = '' then
  begin
     Result := TJSPromise.new(procedure(ASuccess, AFailed: TJSPromiseResolver)
       begin
         Asuccess(Self);
       end);
  end
  else
  begin
    Result := TJSPromise.new(
      procedure(ASuccess, AFailed: TJSPromiseResolver)
      begin
        Application.LoadForm(Self, FormFileName, procedure(AForm: TObject)
          begin
            ASuccess(AForm);
          end);
      end
    );
  end;
end;

function TCustomForm.Execute: TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      ShowModal(procedure(AValue: TModalResult)
        begin
          ASuccess(AValue);
        end);
    end
    );
end;

procedure TCustomForm.KeyPreview(var Key: Word; Shift: TShiftState);
var
  i,lt,ft,lti,fti:integer;
begin
  lt := -1;
  ft := $FFFF;

  for i := 0 to ControlCount - 1 do
  begin
    if not (Controls[i].Visible) and (Controls[i].Enabled) then
      Continue;

    if Controls[i].TabOrder < ft then
    begin
      ft := Controls[i].TabOrder;
      fti := i;
    end;

    if Controls[i].TabOrder > lt then
    begin
      lt := Controls[i].TabOrder;
      lti := i;
    end;

    // check if control handles Default or Cancel with ESC/Return
    if (Key = VK_RETURN) or (Key = VK_ESCAPE) then
      Controls[i].HandleKeyPreview(Key);
  end;

  if Application.MainForm = Self then
    Exit;

  if (key = VK_TAB) and not (ssShift in Shift) and (lti < ControlCount) and (fti >= 0) and (fti <> lti) then
  begin
    if Controls[lti].Focused then
    begin
      Controls[fti].SetFocus;
      key := 0;
    end;
  end;

  if (key = VK_TAB) and (ssShift in Shift) and (fti >= 0) and (lti < ControlCount) and (fti <> lti) then
  begin
    if Controls[fti].Focused then
    begin
      Controls[lti].SetFocus;
      key := 0;
    end;
  end;



end;

function TCustomForm.LoadAndExecute: TJSPromise;

  function LoadAsync: boolean; async;
  begin
    Result := await(boolean, Load);
  end;

begin
  LoadAsync;

  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)
    begin
      ShowModal(procedure(AValue: TModalResult)
        begin
          ASuccess(AValue);
        end);
    end
    );
end;

procedure TCustomForm.BindEvents;
var
  pc: TPersistentClass;
  hash: string;
begin
  inherited;

  if (document.readyState <> 'loading') then
    if Assigned(OnDOMContentLoaded) then
      OnDomContentLoaded(Self);

  window.addEventListener('resize', FResizePtr);
  window.addEventListener('load', FLoadedPtr);
  window.addEventListener('DOMContentLoaded', FDOMContentLoadedPtr);
  document.addEventListener('scroll', FScrollPtr);
  window.addEventListener('unload', FUnloadPtr);
  window.addEventListener('beforeunload', FBeforeUnloadPtr);
  window.addEventListener('keydown', FKeyDownPtr);
  window.addEventListener('orientationchange',FOrientationChangePtr);
  window.addEventListener('hashchange',FHashChangePtr);

  pc := GetClass(ClassName);

  if Assigned(pc) and not Popup and Application.FRouting then
  begin
    hash := ClassName;
    delete(hash,1,1);
    LastHash := hash;
    window.location.hash := hash;
  end;
end;

procedure ReleaseForm(AForm: TCustomForm);
begin
  AForm.Free;
  AForm := nil;
end;

procedure TCustomForm.ReadPixelsPerInch(AReader: TReader);
begin
end;

procedure TCustomForm.WritePixelsPerInch(AWriter: TWriter);
begin
end;

procedure TCustomForm.DefineProperties(AFiler: TFiler);
const
  cPixelsPerInch = 'PixelsPerInch';
begin
  inherited DefineProperties(AFiler);
  AFiler.DefineProperty(cPixelsPerInch, @ReadPixelsPerInch, @WritePixelsPerInch, False);
end;

procedure TCustomForm.ClearMethodPointers;
begin
  inherited;
  FLoadedPtr := nil;
  FDOMContentLoadedPtr := nil;
  FUnloadPtr := nil;
  FBeforeUnloadPtr := nil;
  FResizePtr := nil;
  FScrollPtr := nil;
  FDocMouseUpPtr := nil;
  FDocMouseMovePtr := nil;
  FTitleDownPtr := nil;
  FDoClickPtr := nil;
  FKeyDownPtr := nil;
  FOrientationChangePtr := nil;
  FHashChangePtr := nil;
end;

procedure TCustomForm.AddCSS(const id: string; const css: string);
begin
  asm
    function writeStylesOnce(styleName, cssText) {
        var styleElement = document.getElementById(styleName);
        if (styleElement) {
             styleElement.innerHTML = cssText;
          return;
          }
        styleElement = document.createElement('style');
        styleElement.type = 'text/css';
        styleElement.id = styleName;
        styleElement.innerHTML = cssText;
        document.getElementsByTagName('head')[0].appendChild(styleElement);
    }
    writeStylesOnce(id,css);
  end;
end;

procedure TCustomForm.RemoveCSS(const id: string);
var
  el: TJSElement;
begin
  el := document.getElementById(id);
  if Assigned(el) then
    document.head.removeChild(el);
end;

procedure TCustomForm.Close;
var
  lAction: TCloseAction;
  el: TJSElement;
begin
  if CloseQuery then
  begin
    UnbindEvents;

    lAction := caHide;

    if FTimerID <> -1 then
    begin
      window.clearInterval(FTimerID);
      FTimerID := -1;
    end;

    if Assigned(OnClose) then
      OnClose(Self, lAction);

    //if lAction = caFree then
    //  DestroyControls;

    if lAction <> caNone then
    begin
      if Assigned(FLayer) then
      begin
        if Container.parentNode = FLayer then
        begin
          FLayer.removeChild(Container);
        end
        else
        begin
          if Container.parentNode.parentNode = FLayer then
            FLayer.removeChild(Container.parentNode);
        end;

        Container := nil;
        if FormContainerElement = FLayer.parentNode then
        begin
          FormContainerElement.removeChild(FLayer);
          Application.ChangeMaxZIndex(-1);
        end;
        FLayer := nil;
      end;

      if (FormFileName = '') then
      begin
        if (FFormElement <> '') and Assigned(Container) then
        begin
          Container.innerHTML := '';
          Container := nil;
        end
        else
          if Assigned(Container) then
          begin
            if FormContainerElement = Container.parentNode then
              FormContainerElement.removeChild(Container);
            Container := nil;
          end;
      end
      else
      begin
        if FFormElement <> '' then
        begin
          Container.innerHTML := '';
          Container := nil;
        end
      end;

      if (FFormElement <> FormContainer) and (FFormElement <> '') then
      begin
        el := document.getElementById(FFormElement);
        if Assigned(el) then
          el.innerHTML := '';
      end;

      DoClose(lAction);

      if (lAction = caFree) and not (csDestroying in ComponentState) then
      begin
        Visible := false;
        ReleaseForm(Self);
        if Application.FMainForm = Self then
          Application.FMainForm := nil;
      end;

      Application.FActiveForm := FPrevActiveForm;

      Visible := False;
    end;
  end;
end;

function TCustomForm.CloseQuery: Boolean;
begin
  Result := True;
  if Assigned(FOnCloseQuery) then
    FOnCloseQuery(Self, Result);
end;

procedure TCustomForm.ColorChanging;
begin
  inherited;
  if Assigned(Container) then
  begin
    TJSHTMLElement(Container).style.setProperty(CSSBackground, ColorToHTML(Color));
  end;
end;

function TCustomForm.ContainerElement: TJSElement;
begin
  Result := FormContainerElement;
end;

constructor TCustomForm.Create(AOwner: TComponent);
begin
  FNoHTML := false;
  FCreating := true;
  FResizeObserver := nil;
  inherited;
  FBorder := fbSingle;
  FFormFileName := '';
  FPopupMode := pmNone;
  FPopupClose := pcOnDeactivate;
  FPopup := True;
  FPosition := poScreenCenter;
  FPopupOpacity := 1.0;
end;

constructor TCustomForm.Create(AOwner: TComponent; IsPopup: boolean; ABorder: TFormBorderStyle; ATitle: string);
begin
  FNoHTML := false;
  FCreating := true;
  FResizeObserver := nil;
  inherited Create(AOwner);
  FBorder := ABorder;
  FCaption := ATitle;
  FFormFileName := '';
  FPopupMode := pmNone;
  FPopupClose := pcOnDeactivate;
  FPopup := IsPopup;
  FPosition := poScreenCenter;
  FPopupOpacity := 1.0;
end;

function TCustomForm.CreateCaption: TJSHTMLElement;
var
  BarHeight: string;
begin
  BarHeight := IntToStr(Application.FormCaptionHeight);
  FCaptionElement := TJSHTMLElement(document.createElement('DIV'));
  FCaptionElement.innerHTML := Caption;
  FCaptionElement.style.setProperty(CSSBackground, ColorToHTML(Application.ThemeColor));
  FCaptionElement.style.setProperty('color', ColorToHTML(Application.ThemeTextColor));
  FCaptionElement.style.setProperty('cursor', 'move');
  //title.style.setProperty('padding', Padding + 'px');
  FCaptionElement.style.setProperty('height', BarHeight + 'px');
  FCaptionElement.style.setProperty('line-height', BarHeight + 'px');
  FCaptionElement.style.setProperty('border-bottom', '1px solid black');
  FCaptionElement.style.setProperty('padding-left','4px');

  FCaptionElement.style.setProperty('-moz-user-select','none');
  FCaptionElement.style.setProperty('-webkit-user-select','none');
  FCaptionElement.style.setProperty('-ms-user-select','none');
  FCaptionElement.style.setProperty('user-select','none');
  FCaptionElement.style.setProperty('-o-user-select','none');

  if ElementCaptionClassName <> '' then
    FCaptionElement.setAttribute('class',ElementCaptionClassName);

  SetHTMLElementFont(FCaptionElement, Font, not ((ElementCaptionClassName = '') and (ElementFont = efProperty)));

  if not Assigned(FTitleDownPtr) then
    GetMethodPointers;

  FCaptionElement.innerHTML := FCaption;
  FCaptionElement.addEventListener('mousedown', FTitleDownPtr);
  FHasCaption := true;

  Result := FCaptionElement;
end;

procedure TCustomForm.CreateControl;
begin
  if not Visible and not FPopup then
    Exit;
  inherited;
  UpdateSize;
end;

function TCustomForm.CreateLayer: TJSHTMLElement;
begin
  Result := TJSHTMLElement(document.createElement('SPAN'));
  Result.style.setProperty('top', '0');
  Result.style.setProperty('left', '0');
  Result.style.setProperty('right', '0');
  Result.style.setProperty('bottom', '0');

  Result.style.setProperty('webkit-user-select', 'none');
  Result.style.setProperty('moz-user-select', 'none');
  Result.style.setProperty('khtml-user-select', 'none');
  Result.style.setProperty('ms-user-select', 'none');
  Result.style.setProperty('user-select', 'none');
  Result.style.setProperty('position', 'absolute');
  Result.style.setProperty('z-index', Application.MaxZIndexStr);
end;

function TCustomForm.CreateElement: TJSElement;
var
  eh,caph: TJSHTMLElement;

begin
  if not FPopup and Assigned(FormContainerElement) then
  begin
    Result := FormContainerElement;
    Exit;
  end;

  //if FPopup then
  begin
    Result := document.createElement('DIV');

    FLayer := CreateLayer;
    document.body.appendChild(FLayer);

    eh := TJSHTMLElement(FLayer);

    if FPopupClose = pcOnDeactivate then
    begin
      // ensure method pointers exist
      GetMethodPointers;
      eh.addEventListener('click', FDoClickPtr);
    end;

    eh.style.setProperty(CSSZINDEX, Application.MaxZIndexStr);

    if (Border = fbSizeable) then
    begin
      TJSHTMLElement(Result).style.setProperty('resize','both');
      TJSHTMLElement(Result).style.setProperty('overflow','auto');
    end;

    FHasCaption := false;

    if (Border in [fbSizeable, fbDialog, fbDialogSizeable]) then
    begin
      caph := CreateCaption;
      Result.appendChild(caph);
    end;
  end;
end;

procedure TCustomForm.LoadDFMValues;
begin
end;

procedure TCustomForm.Loaded;
begin
  inherited;
  LoadedDone;
end;

procedure TCustomForm.LoadedDone;
begin
  Application.TriggerNotification;
end;

procedure TCustomForm.Init;
begin
end;

function TCustomForm.IsFocused: Boolean;
begin
  // form is not focused only controls
  Result := false;
end;

function TCustomForm.IsMiletus: boolean;
var
  cl: TClass;
begin
  cl := ClassType;
  Result := false;

  repeat
    cl := cl.ClassParent;
    if Assigned(cl) and (cl.ClassName = 'TMiletusForm') then
    begin
      Result := true;
      break;
    end;
  until not Assigned(cl) or (cl.ClassName = 'TForm');
end;

procedure TCustomForm.Resize;
var
  i,ch: integer;
  neww, newh: integer;
  eh: TJSHTMLElement;
  dr: TJSDOMRect;
begin
  if (csDestroying in ComponentState) then
    Exit;

  FIsResizing := true;

  if FPopup and Assigned(FPopupElement) then
  begin
    eh := FPopupElement;
    dr := eh.getBoundingClientRect;

    neww := Round(dr.Right - dr.Left - 2);
    if (Border in [fbDialog, fbDialogSizeable]) then
    begin
      ch := GetCaptionHeight;
      newh := Round(dr.Bottom - dr.Top - ch - 2)
    end
    else
      newh := Round(dr.Bottom - dr.Top - 2)
  end
  else
  begin
    neww := window.innerWidth;
    newh := window.innerHeight;
  end;

  UpdateControlSize(neww, newh);

  if not (IsAndroid and (document.activeElement.tagName = 'INPUT')) then
    AlignControl(Self);

  DoResize;

  FIsResizing := false;

  if not IsUpdating then
  begin
    if (OrigRect.Left = -1) and (OrigRect.Top = -1) then
      InitAnchoring;

    for i := 0 to ControlCount - 1 do
      Controls[i].UpdateAnchoring;
  end;

end;

procedure TCustomForm.SetActiveControl(const Value: TWinControl);
begin
  FActiveControl := Value;
  if Assigned(FActiveControl) then
    FActiveControl.SetFocus;
end;

procedure TCustomForm.SetBorder(const Value: TFormBorderStyle);
begin
  if (FBorder <> Value) then
  begin
    FBorder := Value;
    if Assigned(Container) then
      AlignControl(Self);
  end;
end;

procedure TCustomForm.SetBounds(X, Y, AWidth, AHeight: Integer);
begin
  if (csDesigning in ComponentState) then
  begin
    Resize;
    SetBoundsInt(X, Y, AWidth, AHeight);
  end
  else
    inherited SetBounds(X, Y, AWidth, AHeight);
end;

procedure TCustomForm.SetCaption(const AValue: string);
begin
  if FCaption <> AValue then
  begin
    FCaption := AValue;
    UpdateElement;
  end;
end;

procedure TCustomForm.SetColorScheme(AScheme: TApplicationColorScheme);
var
  s: string;
begin
  if not Application.Themed then
    Exit;

  case AScheme of
  csDark:
    begin
      Color := $202124;
      s :=
        'label,option,td,table,ul,li {background-color: #202124; color: #BEC1C6;}'+#13#10 +
        'body,input,textarea,select,fieldset {background-color: #202124; color: #BEC1C6; border: 1px solid gray;}'+#13#10 +
        'input:focus, textarea:focus, select:focus, span:focus { border: 1px solid silver; outline: none !important;}';
      AddCSS('appscheme',s);
    end;
  csLight:
    begin
      Color := clWhite;
      RemoveCSS('appscheme');
    end;
  end;
end;

procedure TCustomForm.SetElementClassName(AValue: string);
begin
  inherited;
  if FPopup and Assigned(FPopupElement) then
  begin
    FPopupElement['class'] := AValue;
  end;
end;

procedure TCustomForm.SetFormContainer(const Value: TElementID);
var
  el,old: TJSElement;
  i: integer;

begin
  old := Container;

  if (csDesigning in ComponentState) then
    FFormContainer := Value
  else
  begin
    if Uppercase(Value) = 'BODY' then
      FFormContainer := ''
    else
      FFormContainer := Value;
  end;

  if (csLoading in ComponentState) and not (csDesigning in ComponentState) then
    Exit;

   // displayed on top of other in layer
  if Assigned(FLayer) then
    el := FLayer
  else
   // update container element
  if Value = '' then
  begin
    el := document.body;
  end
  else
  begin
    el := document.getElementById(Value);
    if not Assigned(el) then
      el := document.body;
  end;

  if Assigned(el) then
  begin
    Container := el;

    if Assigned(old) then
    begin
      for i := 0 to ControlCount - 1 do
      begin
        if (Controls[i].ParentElement = old) then
          Controls[i].ParentElementHandle := TJSHTMLElement(el);

        if Assigned(Controls[i].ElementHandle) and (Controls[i].ElementHandle.parentElement = old) then
        begin
          el.appendChild(Controls[i].ElementHandle);
        end;
      end;
    end;
  end;
end;

procedure TCustomForm.SetFormPos;
begin
  if not Assigned(Container) then
    Exit;

  if Position = poScreenCenter then
  begin
    FLeft := Round((WinWidth - Width)/2) + window.scrollX;
    FTop := Round((WinHeight - Height)/2) +  window.scrollY;
  end;

  TJSHTMLElement(Container).style.setProperty('position','absolute');
  TJSHTMLElement(Container).style.setProperty('width',inttostr(Width)+'px');
  TJSHTMLElement(Container).style.setProperty('height',inttostr(Height)+'px');
  TJSHTMLElement(Container).style.setProperty('left',inttostr(Left)+'px');
  TJSHTMLElement(Container).style.setProperty('top',inttostr(Top)+'px');
end;

procedure TCustomForm.SetFormStyle(const Value: TFormStyle);
begin
  FFormStyle := Value;
end;

procedure TCustomForm.SetClientHeight(const Value: Integer);
begin
  if (Self.Border in [fbDialog, fbDialogSizeable]) then
  begin
    SetHeight(Value + Application.FormCaptionHeight);
  end
  else
    inherited;
end;

procedure TCustomForm.SetHeight(AValue: Integer);
var
  eh: TJSHTMLElement;
begin
  if ((csLoading in ComponentState) and (FFormContainer <> '')) or (not Popup and not Assigned(ElementHandle)) then
  begin
    SetHeightInt(AValue);
    if Assigned(FormContainerElement) then
    begin
      if (Uppercase(FormContainerElement.tagName) <> 'BODY') and not IsLinked then
        TJSHTMLElement(FormContainerElement).style.setProperty('height',inttostr(AValue)+'px');
    end;
    Exit;
  end;

  inherited;

  if (FFormContainer <> '') and Assigned(FormContainerElement) then
  begin
    TJSHTMLElement(FormContainerElement).style.setProperty('height',inttostr(AValue)+'px');
    Realign;
  end;

  if Popup then
  begin
    eh := FPopupElement;
    if Assigned(eh) then
      eh.style.setProperty('height',inttostr(AValue)+'px');
  end;
end;

procedure TCustomForm.SetLeft(AValue: Integer);
var
  eh: TJSHTMLElement;
begin
  inherited;
  if Popup then
  begin
    eh := FPopupElement;
    if Assigned(eh) then
      eh.style.setProperty('left',inttostr(avalue)+'px');
  end;
end;

procedure TCustomForm.SetModalResult(const Value: TModalResult);
begin
  FModalResult := Value;
  if (FModalResult <> mrNone) then
    Close;
end;

procedure TCustomForm.SetPosition(const Value: TPosition);
begin
  FPosition := Value;
end;

procedure TCustomForm.SetShadow(const Value: boolean);
begin
  if (FShadow <> Value) then
  begin
    FShadow := Value;
    UpdateElement;
  end;
end;

procedure TCustomForm.SetTop(AValue: Integer);
var
  eh: TJSHTMLElement;
begin
  inherited;
  if Popup then
  begin
    eh := FPopupElement;
    if Assigned(eh) then
      eh.style.setProperty('top',IntToStr(avalue)+'px');
  end;
end;

procedure TCustomForm.SetWidth(AValue: Integer);
var
  eh: TJSHTMLElement;
begin
  if ((csLoading in ComponentState) and (FFormContainer <> '')) or (not Popup and not Assigned(ElementHandle)) then
  begin
    SetWidthInt(AValue);
    Exit;
  end;

  inherited;

  if Popup then
  begin
    eh := FPopupElement;
    if Assigned(eh) then
      eh.style.setProperty('width',IntToStr(avalue)+'px');
  end;
end;

procedure TCustomForm.SelectFirst;
begin
  if ControlCount > 0 then
    Controls[0].SetFocus;
end;

procedure TCustomForm.SelectNext(CurControl: TControl; GoForward: boolean; CheckTabStop: boolean);
var
  i: integer;
begin
  if ControlCount > 0 then
  begin
    for i := 0 to ControlCount - 1 do
    begin
      if Controls[i] = CurControl then
      begin
        if GoForward then
        begin
          if i + 1 <= ControlCount - 1  then
            Controls[i + 1].SetFocus
          else
            Controls[0].SetFocus;
        end
        else
        begin
          if i - 1 >= 0 then
            Controls[i - 1].SetFocus
          else
            Controls[ControlCount - 1].SetFocus;
        end;
        break;
      end;
    end;
  end;
end;

procedure TCustomForm.Show;
var
  dr: TJSDOMRect;
begin
  if FCreating and FPopup then
  begin
    Application.ChangeMaxZIndex(+1);
    FCreating := false;
    UpdateElement;
  end;

  if (FormFileName = '') then
  begin
    if not Assigned(Container) then
    begin
      if FPopup then
      begin
        Container := CreateElement;
//        TJSHTMLElement(FLayer).style.setProperty(CSSBackground,'silver');
        FPopupElement := TJSHTMLElement(Container);
        SetFormPos;
        UpdateElement;
        FLayer.appendChild(Container);
      end
      else
      begin
        Container := CreateLayer;
        TJSHTMLElement(Container).style.setProperty(CSSBackground,ColorToHTML(Color));
        document.body.appendChild(Container);
      end;
    end
    else
    begin
      if FPopup then
      begin
        SetFormPos;
        FLayer.appendChild(Container)
      end
      else
      begin
        if Assigned(FLayer) then
        begin
          FLayer.appendChild(Container)
        end
        else
        begin
          document.body.appendChild(Container);
        end;
      end;
    end;

    if FPopup then
    begin
      if (Border = fbSizeable) then
      begin
        dr := Container.getBoundingClientRect;
        FOrigWidth := Round(dr.Right - dr.Left);
        FOrigHeight := Round(dr.Bottom - dr.Top);
        FTimerID := window.setInterval(@HandleDoResize, 100);
      end
      else
      begin
        TJSHTMLElement(Container).style.setProperty('resize','');
        TJSHTMLElement(Container).style.setProperty('overflow','');
      end;
    end;
  end;

  Visible := True;

  DoShow;
end;

function TCustomForm.ShowModal: Integer;
begin
  Result := ShowModal(nil);
end;

function TCustomForm.ShowModal(AProc: TModalResultProc): TModalResult;
begin
  // create runtime form without HTML
  if FNoHTML or (FormFileName = '') then
  begin
    FPrevActiveForm := Application.ActiveForm;
    Application.FActiveForm := Self;
    Application.CreateNewForm(Self, '');
  end;

  Result := mrNone;
  FNoHTML := false;
  FModalProc := AProc;
  ModalResult := mrNone;
  Show;
end;

procedure TCustomForm.UnbindEvents;
begin
  inherited;

  if Assigned(FLayer) and (FPopupClose = pcOnDeactivate) then
    FLayer.removeEventListener('click', FDoClickPtr);

  if Assigned(FResizeObserver) then
  begin
    asm
      this.FResizeObserver.disconnect();
    end;
  end;

  window.removeEventListener('resize', FResizePtr);
  window.removeEventListener('load', FLoadedPtr);
  window.removeEventListener('DOMContentLoaded',FDOMContentLoadedPtr);
  document.removeEventListener('scroll', FScrollPtr);
  window.removeEventListener('unload', FUnloadPtr);
  window.removeEventListener('beforeunload', FBeforeUnloadPtr);
  window.removeEventListener('keydown', FKeyDownPtr);
  window.removeEventListener('orientationchange',FOrientationChangePtr);
  window.removeEventListener('hashchange',FHashChangePtr);
end;

procedure TCustomForm.UpdateCSS(const id, css: string);
begin
  RemoveCSS(id);
  AddCSS(id,css);
end;

procedure TCustomForm.UpdateElement;
var
  clr: string;
  el: TJSNode;

begin
  if FCreating then
    Exit;

  inherited;

  if IsUpdating then
    Exit;

  if Assigned(ElementHandle) or Assigned(Container) then
  begin
    if (FCaption <> '') then
    begin
      if not FPopup then
        window.document.title := FCaption
      else
      begin
        if Assigned(FCaptionElement) then
        begin
          el := FCaptionElement.childNodes[0];
          if Assigned(el) then
             el.nodeValue := FCaption;

          if ShowClose and not Assigned(FCaptionCloseElement) then
          begin
            FCaptionCloseElement := TJSHTMLElement(document.createElement('SPAN'));
            FCaptionCloseElement.style.setProperty('float','right');
            FCaptionCloseElement.style.setProperty('cursor','default');
            FCaptionCloseElement.style.setProperty('margin-right','8px');
            FCaptionCloseElement.innerHTML := '&#x2716;';
            FCaptionElement.appendChild(FCaptionCloseElement);
            FCaptionCloseElement.addEventListener('click', @HandleCloseClick);
          end;

          SetHTMLElementFont(FCaptionElement, Font, not ((ElementCaptionClassName = '') and (ElementFont = efProperty)));
        end;
      end;
    end;
  end;


  if Assigned(Container) then
  begin
    clr := ColorToHtml(Color);

    if Assigned(Container) then
    begin
      if (Color <> clNone) and ((Color <> clWhite) and not Popup) and (ElementClassName = '') then
        TJSHTMLElement(Container).style.setProperty(CSSBackground,clr);

      if (ElementClassName <> '') or (Color = clNone) or ((Color = clWhite) and not Popup) then
        TJSHTMLElement(Container).style.removeProperty(CSSBackground);

      if (ElementClassName = '') and not Popup then
      begin
        // make sure body stretches over the entire page
        //TJSHTMLElement(Container).style.setProperty('height','100%');
        asm
          document.documentElement.style.setProperty('height','100%');
        end;
      end;

      SetHTMLElementFont(TJSHTMLElement(Container), Font, not ((ElementClassName = '') and (ElementFont = efProperty)));
    end;

    if FPopup then
    begin
      if not Assigned(FPopupElement) then
        FPopupElement := TJSHTMLElement(Container);

      if ElementPopupClassName <> '' then
      begin
        TJSHTMLElement(Container).style.removeProperty('border');
        TJSHTMLElement(FPopupElement).setAttribute('class', FElementPopupClassName);
      end
      else
      begin
        if FShadow then
        begin
          TJSHTMLElement(FPopupElement).style.setProperty('box-shadow','3px 3px 3px silver')
        end
        else
        begin
          TJSHTMLElement(FPopupElement).style.removeProperty('box-shadow');
        end;

        TJSHTMLElement(FPopupElement).style.setProperty('border','1px solid gray');
      end;
    end
    else
    begin
      TJSHTMLElement(Container).style.removeProperty('box-shadow');
    end;
  end;
end;

procedure TCustomForm.VisibleChanged;
var
  LIsBody: boolean;
begin
  inherited;

  if Assigned(FLayer) then
  begin
    if Visible then
      TJSHTMLElement(FLayer).style.setProperty('display','')
    else
      TJSHTMLElement(FLayer).style.setProperty('display','none');
  end
  else
  begin
    LIsBody := LowerCase(ElementID) = cBodyTag;

    if Assigned(Container) and (FormContainer <> '') and not LIsBody then
    begin
      if Visible then
        TJSHTMLElement(Container).style.setProperty('display','')
      else
        TJSHTMLElement(Container).style.setProperty('display','none');
    end;
  end;
end;


procedure TCustomForm.Paint;
begin
  //
end;

procedure TCustomForm.PreventDefault;
begin
  ElementEvent.preventDefault();
end;

function TCustomForm.HandleObserver: boolean;
begin
  Resize;
  Result := true;
end;

function TCustomForm.HandleOrientationChange(Event: TJSEvent): Boolean;
var
  so: TScreenOrientation;
  isPortrait: boolean;
begin
  Resize;
  Realign;
  Result := true;

  if Assigned(OnOrientationChange) then
  begin
    asm
      var mql = window.matchMedia("(orientation: portrait)");
      isPortrait = (mql.matches);
    end;

    if isPortrait then
      so := soLandscape
    else
      so := soPortrait;

    OnOrientationChange(Self, so);
  end;

  if Assigned(OnResize) then
    OnResize(Self);
end;

function TCustomForm.HandleResize(Event: TEventListenerEvent): boolean;
begin
  if Visible and not (csDestroying in ComponentState) then
  begin
    if not Assigned(Container) then
      CreateControl;

    Resize;

    // retrigger in case resize changed scrollbar visibility
    if (ElementHandle = document.body) then
      window.setTimeout(@Resize, 25);
  end;

  // make sure HandleObserver is not optimized away
  if (1<0) then
  begin
    HandleObserver;
  end;

  Result := true;
end;

function TCustomForm.GetActiveControl: TWinControl;
var
  i: integer;
begin
  Result := FActiveControl;


  for i := 0 to ControlCount - 1 do
  begin
    if Controls[i].ElementHandle = document.activeElement then
    begin
      Result := TWinControl(Controls[i]);
      break;
    end;

  end;
end;

function TCustomForm.GetCanvas: TCanvas;
begin
  Result := nil;
end;

function TCustomForm.GetCaptionHeight: integer;
var
  r: TJSDOMRect;
begin
  Result := 0;
  if Assigned(FCaptionElement) and (Border in [fbDialog, fbDialogSizeable]) then
  begin
    r := FCaptionElement.getBoundingClientRect;
    Result := Round(r.Bottom - r.Top);
  end;
end;

function TCustomForm.GetClientRect: TRect;
//var
//  ch: integer;
begin
  Result := inherited GetClientRect;

//  if FHasCaption then
//  begin
//    ch := GetCaptionHeight - 2;
//    Result.Top := Result.Top + ch;
//    Result.Bottom := Result.Bottom + ch;
//  end;
end;

function TCustomForm.GetElementBindHandle: TJSEventTarget;
begin
  if (csDesigning in ComponentState) then
  begin
    Result := TJSEventTarget(window);
    Exit;
  end;

  if (FormContainer <> '') then
  begin
    Result := TJSEventTarget(window);
  end
  else
  begin
    if not FPopup then
    begin
      Result := TJSEventTarget(window);
    end
    else
      Result := inherited GetElementBindHandle;
  end;
end;

function TCustomForm.GetElementHandle: TJSHTMLElement;
begin
  if FPopup then
    Result := inherited GetElementHandle
  else
    Result := TJSHTMLElement(FormContainerElement);
end;

function TCustomForm.GetFormStyle: TFormStyle;
begin
  Result := FFormStyle;
end;

function TCustomForm.GetClientHeight: Integer;
begin
  if (Self.Border in [fbDialog, fbDialogSizeable]) then
  begin
    Result := Height - Application.FormCaptionHeight;
  end
  else
    Result := inherited;
end;

function TCustomForm.GetHeight: Integer;
var
  d: integer;
  s: string;
  css: TJSCSSStyleDeclaration;
  el: TJSHTMLElement;
  dr: TJSDOMRect;
begin
  if (FFormElement <> '') then
  begin
    el := TJSHTMLElement(document.getElementById(FFormElement));
    if Assigned(el) then
    begin
      dr := el.getBoundingClientRect;
      Result := Round(dr.bottom - dr.top);
      Exit;
    end
  end;

  if (ElementHandle = document.body) then
  begin
    css := window.getComputedStyle(ElementHandle);
    s := css.getPropertyValue('margin-top');
    s := Copy(s,1,Length(s) - 2);
    d := Round(StrToFloat(s, TFormatSettings.Invariant) - 2); // div 2;

    dr := document.documentElement.getBoundingClientRect;
    Result := Round(dr.Height);

    if Result > window.innerHeight then
      Result := window.innerHeight - Max(0,d);
  end
  else
  begin
    if FPopup then
    begin                            // take caption border in account
      Result := inherited GetHeight  // GetCaptionHeight - 2;
    end
    else
    begin
      Result := Round(ElementHandle.offsetHeight);
      if Result = 0 then
      begin
        if FFormContainer <> '' then
          Result := inherited GetHeight
        else
          Result:= window.innerHeight;
      end;
    end;
  end;
end;

class function TCustomForm.GetHTMLFileName: string;
begin
  Result := Self.UnitName + Application.GetFormExtension;
end;

function TCustomForm.GetLeft: Integer;
var
  el: TJSHTMLElement;
  dr: TJSDOMRect;
begin
  if FPopup then
  begin
    Result := inherited GetLeft
  end
  else
  begin
    if (FFormElement <> '') then
    begin
      el := TJSHTMLElement(document.getElementById(FFormElement));
      if Assigned(el) then
      begin
        dr := el.getBoundingClientRect;
        Result := Round(dr.left);
      end;
    end
    else
    begin
      el := nil;
      if (FormContainer <> '') then
        el := TJSHTMLElement(document.getElementById(FormContainer));

      if not Assigned(el) then
         el := ElementHandle; 

      Result := Round(el.offsetLeft);
    end;
  end;
end;

procedure TCustomForm.GetMethodPointers;
begin
  inherited;

  if FLoadedPtr = nil then
  begin
    FLoadedPtr := @HandleLoaded;
    FDOMContentLoadedPtr := @HandleDOMContentLoaded;
    FUnloadPtr := @HandleUnLoad;
    FBeforeUnloadPtr := @HandleBeforeUnload;
    FResizePtr := @HandleResize;
    FScrollPtr := @HandleScroll;
    FDocMouseUpPtr := @HandleDocMouseUp;
    FDocMouseMovePtr := @HandleDocMouseMove;
    FTitleDownPtr := @HandleTitleDown;
    FDoClickPtr := @HandleDoClick;
    FKeyDownPtr := @HandleKeyDown;
    FOrientationChangePtr := @HandleOrientationChange;
    FHashChangePtr := @HandleHashChange;
  end;
end;

function TCustomForm.GetTop: Integer;
var
  el: TJSHTMLElement;
  dr: TJSDOMRect;
begin
  if FPopup then
    Result := inherited GetTop
  else
  begin
    if (FFormElement <> '') then
    begin
      el := TJSHTMLElement(document.getElementById(FFormElement));
      if Assigned(el) then
      begin
        dr := el.getBoundingClientRect;
        Result := Round(dr.top);
      end;
    end
    else
    begin
      el := nil;
      if (FormContainer <> '') then
        el := TJSHTMLElement(document.getElementById(FormContainer));

      if not Assigned(el) then
          el := ElementHandle; 
      Result := Round(el.offsetTop);
    end; 
  end;
end;

function TCustomForm.GetUniqueComponentName(AComponent: TComponent): string;
var
  s: string;
  i,j: integer;
  found: boolean;
begin
  i := 1;
  repeat
    found := false;

    s := Copy(ClassName,2, Length(ClassName)) + '_' + Copy(AComponent.ClassName, 2, Length(AComponent.ClassName)) + IntToStr(i);
    inc(i);

    for j := 0 to ComponentCount - 1 do
    begin
      if Components[j].Name = s then
      begin
        found := true;
        break;
      end;
    end;

  until found = false;

  Result := s;
end;

function TCustomForm.GetWidth: Integer;
var
  dr: TJSDOMRect;
  el: TJSHTMLElement;
//  s: string;
//  d: integer;
//  css: TJSCSSStyleDeclaration;
begin
  if (FFormElement <> '') then
  begin
    el := TJSHTMLElement(document.getElementById(FFormElement));
    if Assigned(el) then
    begin
      dr := el.getBoundingClientRect;
      Result := Round(dr.right - dr.left);
      Exit;
    end
  end;

  if (ElementHandle = document.body) then
  begin
    {
    css := window.getComputedStyle(ElementHandle);
    s := css.getPropertyValue('margin-left');
    s := Copy(s,1,Length(s) - 2);
    d := StrToInt(s) - 2;
    Result := window.innerWidth - d + 4;
    }
    Result := window.innerWidth;

    dr := document.documentElement.getBoundingClientRect;
    Result := Round(dr.Width);

    if Result > window.innerWidth then
      Result := window.innerWidth;
  end
  else
  begin
    if FPopup then
      Result := inherited GetWidth
    else
    begin

      Result := Round(ElementHandle.offsetWidth);
      if Result = 0 then
      begin
        if FFormContainer <> '' then
          Result := inherited GetWidth
        else
          Result := window.innerWidth;
      end;
    end;
  end;
end;

function TCustomForm.HandleBeforeUnload(Event: TEventListenerEvent): boolean;
var
  msg: string;
begin
  msg := '';
  if Assigned(OnBeforeUnload) then
    OnBeforeUnload(Self,msg);

    if msg <> '' then
    begin
      asm
        Event.returnValue = msg;
        return msg;
      end;
    end;

  Result := true;
end;

function TCustomForm.HandleCloseClick(Event: TEventListenerEvent): boolean;
var
  CanClose: boolean;

begin
  CanClose := true;

  if Assigned(OnCloseQuery) then
   OnCloseQuery(self, CanClose);

  if CanClose then
    Close;

  Result := true;
end;

function TCustomForm.HandleDoClick(Event: TJSMouseEvent): Boolean;
begin
  Event.stopPropagation;

  if FPopupClose = pcOnDeactivate then
    Close;

  Result := True;
end;

function TCustomForm.HandleDocMouseMove(Event: TJSMouseEvent): Boolean;
var
  deltax,deltay: double;
  el: TJSHTMLElement;
  l,t: integer;
begin
  if FDown and Assigned(Container) then
  begin
    deltax := Event.clientX - FMdx;
    deltay := Event.clientY - FMdy;

    if FHasCaption and (FormFileName <> '') then
      el := TJSHTMLElement(Container.parentNode)
    else
      el := TJSHTMLElement(Container);

    el.style.setProperty('transform','');
    el.style.setProperty('position','absolute');

    l := round(FDlgX + deltax);
    t := round(FDlgY + deltay);

    el.style.setProperty('left', inttostr(l)+'px');
    el.style.setProperty('top', inttostr(t)+'px');

    FLeft := l;
    FTop := t;

//    el := TJSHTMLElement(FCancel);
//    el.style.setProperty('position', 'absolute');
//    el.style.setProperty('top', '0');
//    el.style.setProperty('right', '0');
  end;

  Result := true;
end;

function TCustomForm.HandleDocMouseUp(Event: TJSMouseEvent): Boolean;
begin
  if FDown then
  begin
    FCaptured := false;
    FDown := false;
    document.body.removeEventListener('mousemove', FDocMouseMovePtr);
    document.body.removeEventListener('mouseup', FDocMouseUpPtr);
    if Assigned(FMoveSpan) then
      document.body.removeChild(FMoveSpan);

    EventStopPropagation :=   EventStopPropagation + [eeMouseMove, eeMouseUp];

    FMoveSpan := nil;
  end;
  Result := true;
end;

function TCustomForm.HandleDOMContentLoaded(
  Event: TEventListenerEvent): boolean;
begin
  if Assigned(OnDOMContentLoaded) then
    OnDOMContentLoaded(Self);
  Result := true;
end;

procedure TCustomForm.HandleDoResize;
var
  dr: TJSDOMRect;
  neww,newh: integer;
begin
  dr := Container.getBoundingClientRect;

  neww := Round(dr.Right - dr.Left);
  newh := Round(dr.Bottom - dr.Top);

  if (neww <> FOrigWidth) or (newh <> FOrigHeight) then
  begin
    Width := neww;
    Height := newh;

    dr := Container.getBoundingClientRect;
    FOrigWidth := Round(dr.Right - dr.Left);
    FOrigHeight := Round(dr.Bottom - dr.Top);

    AlignControl(Self);
  end;
end;


function TCustomForm.HandleHashChange(Event: TEventListenerEvent): boolean;
var
  oldURL,newURL,newHash: string;
  pc: TPersistentClass;
  newform: TForm;
begin
  asm
    oldURL = Event.oldURL;
    newURL = Event.newURL;
  end;

  if Assigned(OnHashChange) then
  begin
    OnHashChange(Self, oldURL, newURL);
  end;

  if Application.FRouting then
  begin
    NewHash := copy(newURL, pos('#', newURL) + 1, Length(newURL));

    pc := GetClass('T' + Name);

    if Assigned(pc) and (LastHash <> NewHash) then
    begin
      if (pos('#' + Name + '_', oldURL + '_') <> 0) and
         (pos('#' + Name + '_', newURL + '_') = 0) then
      begin
        Close;
      end;

      if not Popup then
      begin
        pc := GetClass('T' + NewHash);
        if Assigned(pc) then
        begin
          Application.AutoCreateForm(TFormClass(pc), newform);
        end;
      end;
    end;
  end;

  Result := true;
end;

function TCustomForm.HandleKeyDown(Event: TJSKeyBoardEvent): Boolean;
var
  k: Word;
begin
  k := GetKeyCode(Event.Key);
  if not Assigned(k) then
    Exit;

  if (k = VK_ESCAPE) and not (csDesigning in ComponentState) and Popup then
  begin
    Close;
  end;
  Result := true;
end;

function TCustomForm.HandleTitleDown(Event: TJSMouseEvent): Boolean;
var
  r: TJSDOMRect;
begin
  Event.StopPropagation;
  Event.PreventDefault;

  FPopupClose := pcNever;

  FMdx := Round(Event.clientX);
  FMdy := Round(Event.clientY);

  if FHasCaption and (FormFileName <> '') then
    r := TJSHTMLElement(Container.parentNode).getBoundingClientRect()
  else
    r := TJSHTMLElement(Container).getBoundingClientRect();

  FDlgX := Round(int(r.left));
  FDlgY := Round(int(r.top));

  FDown := true;

  if not FCaptured then
  begin
    FCaptured := true;
    FMoveSpan := TJSHTMLElement(document.createElement('SPAN'));
    FMoveSpan.style.setProperty('top', '0');
    FMoveSpan.style.setProperty('left', '0');
    FMoveSpan.style.setProperty('right', '0');
    FMoveSpan.style.setProperty('bottom', '0');
    FMoveSpan.style.setProperty('position', 'absolute');

    document.body.appendChild(FMoveSpan);

    EventStopPropagation :=   EventStopPropagation - [eeMouseMove, eeMouseUp];

    document.body.addEventListener('mousemove', FDocMouseMovePtr);
    document.body.addEventListener('mouseup', FDocMouseUpPtr);
  end;

  Result := true;
end;

procedure TCustomForm.DoClose(var CloseAction: TCloseAction);
begin
  if Assigned(FModalProc) and (CloseAction <> caNone) then
  begin
    FModalProc(FModalResult);
    FModalProc := nil;
  end;
end;

procedure TCustomForm.DoCreate;
begin
  BeginUpdate;
  LoadDFMValues;
  Loaded;

  if Assigned(OnCreate) then
    OnCreate(Self);

  FCreating := false;

  EndUpdate;

  UpdateChildren(Self);

  Realign;

  DoResize;
end;

function TCustomForm.HandleScroll(Event: TEventListenerEvent): boolean;
begin
  if Assigned(OnScroll) then
    OnScroll(Self);
  Result := true;
end;

procedure TCustomForm.DoAutoFocus;
var
  i: integer;
begin
  // Auto focus control on form
  for i := 0 to ControlCount - 1 do
  begin
    if (Controls[i].TabOrder = 0) and (Controls[i].TabStop) then
    begin
      Controls[i].SetFocus;
    end;
  end;
end;

procedure TCustomForm.DoShow;
begin
  DoAutoFocus;
  if Assigned(OnShow) and not FShown then
  begin
    FShown := true;
    OnShow(Self);
  end;
end;

function TCustomForm.HandleUnload(Event: TEventListenerEvent): boolean;
begin
  if Assigned(OnUnload) then
    OnUnload(Self);
  Result := true;
end;

function TCustomForm.FormContainerElement: TJSElement;
begin
  if (csDesigning in ComponentState) then
  begin
    if Assigned(FDesignContainer) then
      Result := FDesignContainer
  end;

  if (FormContainer <> '') then
  begin
    Result := document.getElementById(FormContainer);
    if not Assigned(Result) then
      Result := document.body;
  end
  else
  begin
    Result := document.body;
  end;
end;

function TCustomForm.HandleLoaded(Event: TEventListenerEvent): boolean;
begin
  Resize;
  InitAnchoring;
  Result := true;
end;

procedure TCustomForm.Realign;
var
  el: TJSHTMLElement;
  dr: TJSDOMRect;
  r: TRect;
begin
  if (FFormContainer <> '') and (Align <> alNone) then
  begin
    el := TJSHTMLElement( document.getElementById(FFormContainer) );
    if Assigned(el) then
    begin
      dr := el.getBoundingClientRect;

      if (dr.width = 0) and (dr.height = 0) and (el.style.getPropertyValue('position') = 'absolute') then
      begin
        r := GetHTMLElementAbsoluteRect(el);
      end
      else
      begin
        r := Rect(Round(dr.left), Round(dr.Top), Round(dr.Right), Round(dr.Bottom));
      end;

      SetBoundsInt(r.Left, r.Top, r.Right - r.Left, r.Bottom - r.Top);
    end;
  end;

  inherited;
end;

procedure TCustomForm.DoResize;
var
  w,h: integer;
begin
  w := Width;
  h := Height;

  if (w = FOldWidth) and (h = FOldHeight) then
    Exit;

  FOldWidth := w;
  FOldHeight := h;

  if Assigned(OnResize) then
    OnResize(Self);
end;

destructor TCustomForm.Destroy;
begin
  if Visible then
    Close;

  if Assigned(OnDestroy) then
    OnDestroy(Self);

  inherited;

  if Assigned(FLayer) then
  begin
    if FormContainerElement = FLayer.parentNode then
      FormContainerElement.removeChild(FLayer);
    FLayer := nil;
  end;
end;

constructor TCustomForm.Create(id: string);
begin
  FNoHTML := false;
  FCreating := true;
  FFormContainer := id;
  if id = '' then
    inherited Create(cBodyTag)
  else
    inherited Create(id);

  FFormElement := '';
  FModalResult := mrNone;
  FFormStyle := fsNormal;
  DoCreate;
end;

constructor TCustomForm.Create(id: string; var AReference);
begin
  FCreating := true;
  FFormContainer := id;
  inherited Create(id);
  AReference := Self;
  FFormElement := '';
  FModalResult := mrNone;
  FFormStyle := fsNormal;
  DoCreate;
end;

procedure TCustomForm.CreateInitialize;
begin
  inherited;
  AllowTouch := True;
  FModalResult := mrNone;
  FFormStyle := fsNormal;
  FBorder := fbSizeable;
  FShadow := true;
  FShowClose := true;
  FTimerID := -1;
  FCaptionElement := nil;
  FColor := clWhite;
  TJSEventTarget(window).addEventListener('load', FLoadedPtr);
end;

constructor TCustomForm.CreateNew(AOwner: TComponent; Dummy: Integer = 0);
begin
  Create(AOwner);
end;

constructor TCustomForm.CreateDialog(ACaption: string);
var
  AFileName: string;
begin
  FCreating := true;
  AFileName := GetHTMLFileName;
  inherited Create(cBodyTag);
  Caption := ACaption;
  Popup := true;
  Border := fbDialog;
  FormFileName := AFileName;
  Application.LoadForm(Self, AFileName);
end;

constructor TCustomForm.CreateDialogNew(ACaption: string);
var
  AFileName: string;
begin
  FNoHTML := true;
  FCreating := true;
  AFileName := GetHTMLFileName;
  inherited Create(cBodyTag);
  Caption := ACaption;
  Popup := true;
  Border := fbDialog;
  FormFileName := AFileName;
end;

constructor TCustomForm.CreateNew;
var
  AFileName: string;
begin
  FCreating := true;
  AFileName := GetHTMLFileName;
  inherited Create(cBodyTag);
  FormFileName := AFileName;
  if FormFileName <> '' then
    Application.LoadForm(Self, AFileName);
end;

constructor TCustomForm.CreateNew(AFileName: string);
begin
  FCreating := true;
  inherited Create(cBodyTag);
  FormFileName := AFileName;
  if FormFileName <> '' then
    Application.LoadForm(Self, AFileName);
end;

constructor TCustomForm.CreateNew(AProc: TFormCreatedProc);
begin
  FCreatedProc := AProc;
  CreateNew;
end;

constructor TCustomForm.CreateNew(AElementID: string; AProc: TFormCreatedProc);
begin
  CreateNew(AElementID, '', AProc);
end;

constructor TCustomForm.CreateNew(AElementID, AFileName: string; AProc: TFormCreatedProc);
begin
  FCreating := true;
  FFormContainer := AElementID;
  inherited Create(AElementID);
  FCreatedProc := AProc;
  FFormElement := AElementID;
  if AFileName = '' then
    AFileName := GetHTMLFileName;

  if AFileName <> '' then
    Application.LoadForm(Self, AFileName)
  else
    if Assigned(FCreatedProc) then
      FCreatedProc(Self);
end;

{ TApplication }

function TApplication.CreateNewForm(AInstanceClass: TFormClass): TCustomForm;
begin
  if not Assigned(VSIDE) then
    Result := AInstanceClass.Create(cBodyTag)
  else
    Result := AInstanceClass.Create('');
end;

function TApplication.CreateNewForm(AInstanceClass: TFormClass; AElementID: string): TCustomForm;
var
  el: TJSHTMLElement;
  AForm: TForm;
begin
  el := TJSHTMLElement(document.getElementById(AElementID));

  AForm := AInstanceClass.Create(AElementID);
  AForm.FDesignContainer := el;
  AForm.FormContainer := AElementID;
  AForm.FFormElement := AElementID;
  AForm.CreateControl;
  AForm.Container := el;
  AForm.Init;

  Result := AForm;
end;

procedure TApplication.LoadForm(AForm: TCustomForm; AFormFile: string; FormLoaded: TFormCreatedProc);
begin
  FCreatedProc := FormLoaded;
  LoadForm(AForm, AFormFile);
end;

procedure TApplication.LoadForm(AForm: TCustomForm; AFormFile: string);
begin
  AForm.FPrevActiveForm := FActiveForm;

  FActiveForm := AForm;
  FLoadForm := AForm;

  if FMainForm = nil then
    FMainForm := AForm;

  FLastReq := TJSXMLHttpRequest.new;
  FLastReq.addEventListener('load', @DoFormLoad);
  FLastReq.addEventListener('abort', @DoFormAbort);
  FLastReq.open('GET', AFormFile);
  FLastReq.send;
end;

procedure TApplication.Navigate(const AURL: string; ATarget: TNavigationTarget );
begin
  if ATarget = ntBlank then
    window.open(AURL, '_blank')
  else
    window.location.href := AURL;
end;

function TApplication.NeedsFormRouting: Boolean;
var
  s: string;
begin
  Result := HasQueryParam('form', s);
  if Result then
    RouteForm(s);
end;

function TApplication.ObjectURL(AFile: TJSHTMLFile): string;
var
  res: string;
begin
  asm
    res = URL.createObjectURL(AFile);
  end;
  Result := res;
end;

procedure TApplication.CreateFormDirect(AInstanceClass: TFormClass; var AReference);
begin
  CreateForm(AInstanceClass, FAppContainer, AReference, nil, false);
end;

procedure TApplication.CreateFormDirect(AInstanceClass: TFormClass; AElementID: string; var AReference);
begin
  CreateForm(AInstanceClass, AElementID, AReference, nil, false);
end;

procedure TApplication.CreateFormDirect(AInstanceClass: TDataModuleClass; var AReference);
begin
  CreateForm(AInstanceClass, AReference);
end;

procedure TApplication.CreateFormDirect(AInstanceClass: TFrameClass; var AReference);
begin
  CreateForm(AInstanceClass, AReference);
end;

procedure TApplication.CreateForm(AInstanceClass: TFormClass; AElementID: string; var AReference);
begin
  CreateForm(AInstanceClass, AElementID, AReference, nil);
end;

procedure TApplication.CreateForm(AInstanceClass: TFormClass; AElement: TJSHTMLElement; var AReference); overload;
begin
  CreateForm(AInstanceClass, AElement.ID, AReference, nil);
end;

procedure TApplication.CreateForm(AInstanceClass: TFormClass; AElementID: string; var AReference; AProc: TFormCreatedProc);
begin
  CreateForm(AInstanceClass, AElementID, AReference, AProc, true);
end;

procedure TApplication.CreateForm(AInstanceClass: TFormClass; AElementID: string; var AReference; AProc: TFormCreatedProc; LoadHTML: boolean);
var
  LFileName: string;

  function DoStatusCreate(Event: TEventListenerEvent): boolean;
  var
    LElem: TJSHTMLElement;
    LForm: TForm;
    LResponse: string;
    LIsBody: boolean;
    LisLoaded: boolean;
  begin
    LisLoaded := Assigned(Event);
    // HTML of the form being loaded
    if LisLoaded then
    begin
      asm
        LResponse = Event.target.responseText;
      end;

      LIsBody := LowerCase(AElementID) = cBodyTag;

      if LIsBody then
        LElem := TJSHTMLElement(document.body)
      else
        LElem := TJSHTMLElement(document.getElementById(AElementID));

      LElem.innerHTML := LResponse;
    end
    else
    begin
      LElem := TJSHTMLElement(document.body);
    end;

    LForm := AInstanceClass.Create(AElementID, AReference);
    LForm.FormFileName := LFileName;

    if LForm.FormContainer = '' then
      LForm.FormContainer := AElementID;

    if not LIsBody then
    begin
      LForm.FFormElement := AElementID;
      LForm.Container := LElem;
    end;

    LForm.CreateControl;
    LForm.Init;

    LForm.SetColorScheme(Application.ColorScheme);

    // set previous active form
    LForm.FPrevActiveForm := FActiveForm;

    FActiveForm := LForm;

    if FMainForm = nil then
      FMainForm := LForm;

    if Assigned(AProc) then
      AProc(LForm);

    Result := true;

    if LisLoaded then
      ActivateChildScripts(LElem);

    FActiveForm.DoShow;

    // force resize
    FActiveForm.Resize;
  end;

begin
  if FIsRedirect then
    Exit;

  if FAppInitializing then
  begin
    if FCanCreateForm then
      FCanCreateForm := False
    else
      Exit;
  end;

  LFileName := AInstanceClass.GetHTMLFileName;
  if (LFileName <> '') and LoadHTML then
  begin
    FLastReq := TJSXMLHttpRequest.new;
    FLastReq.addEventListener('load', @DoStatusCreate);
    FLastReq.open('GET', lFileName);
    {$IFDEF DEBUG}
    FLastReq.setRequestHeader('Cache-Control', 'no-cache');
    {$ENDIF}
    FLastReq.send;
  end
  else
  begin
    DoStatusCreate(nil);
  end;
end;

procedure TApplication.ActivateChildScripts(AElement: TJSHTMLElement);
begin
  // activate possible child scripts
  asm
    function nodeScriptReplace(node) {
            if ( nodeScriptIs(node) === true ) {
                    node.parentNode.replaceChild( nodeScriptClone(node) , node );
            }
            else {
                    var i        = 0;
                    var children = node.childNodes;
                    while ( i < children.length ) {
                            nodeScriptReplace( children[i++] );
                    }
            }

            return node;
    }
    function nodeScriptIs(node) {
            return node.tagName === 'SCRIPT';
    }
    function nodeScriptClone(node){
            var script  = document.createElement("script");
            script.async = false;
            script.text = node.innerHTML;
            for( var i = node.attributes.length-1; i >= 0; i-- ) {
                    script.setAttribute( node.attributes[i].name, node.attributes[i].value );
            }
            return script;
    }
    nodeScriptReplace(AElement);
  end;
end;

procedure TApplication.ReloadForm;
var
  lFileName: string;

  function DoStatusCreate(Event: TEventListenerEvent): boolean;
  var
    i: integer;
    ctl: TControl;
    s: string;
    sl: TStringList;
    response: string;

  begin
    asm
      response = Event.target.responseText;
    end;

    sl := TStringList.Create;

    // persist control state
    for i := FActiveForm.ControlCount - 1 downto 0 do
    begin
      ctl := FActiveForm.Controls[i];
      s := ctl.SaveState;
      ctl.ElementHandle.Id := ctl.Name;
      //s := ctl.Name +'='+ctl.ElementHandle.outerHTML;
      s := ctl.Name +'='+ s;
      sl.Add(s);
    end;

    for i := FActiveForm.ControlCount - 1 downto 0 do
    begin
      ctl := FActiveForm.Controls[i];
      FActiveForm.RemoveComponent(ctl);
      ctl.Free;
    end;

    FActiveForm.ClearControls;
    FActiveForm.UnbindEvents;

    // HTML of the form being loaded
    document.body.innerHTML := response;

    FActiveForm.CreateControl;
    FActiveForm.DoCreate;
    FActiveForm.Init;

    // persist control state
    for i := FActiveForm.ControlCount - 1 downto 0 do
    begin
      ctl := FActiveForm.Controls[i];  // get controls back after form init
      // get HTML saved for control
      s := sl.Values[ctl.Name];

      if s <> '' then
        ctl.LoadState(s);

      //ctl.ElementHandle.outerHTML := s;
      //ctl.Container := document.getElementById(ctl.Name);
      //ctl.BindEvents;
    end;

    sl.Free;

    FActiveForm.DoShow;

    Result := true;
  end;

begin
  lFileName := FActiveForm.GetHTMLFileName;

  FLastReq := TJSXMLHttpRequest.new;
  FLastReq.addEventListener('load', @DoStatusCreate);
  FLastReq.open('GET', lFileName);
  {$IFDEF DEBUG}
  FLastReq.setRequestHeader('Cache-Control', 'no-cache');
  {$ENDIF}
  FLastReq.send;
end;

function TApplication.HasCSS(href:string): boolean;
var
  els: TJSNodeList;
  i: integer;
  s: string;
begin
  Result := false;
  els := document.querySelectorAll('head > link');
  for i := 0 to els.length - 1 do
  begin
    s := TJSHTMLLinkElement(els[i]).href;

    if CompareText(s, href) = 0 then
    begin
      Result := true;
      break;
    end;
  end;
end;

procedure TApplication.RemoveCSS(id: string);
var
  styleElement: TJSElement;
begin
  styleElement := document.getElementById(id);
  if Assigned(styleElement) then
  begin
    styleElement.parentNode.removeChild(styleElement);
  end;
end;

procedure TApplication.RouteForm(AParameter: string);
var
  frm: TCustomForm;
  fc: TFormClass;
begin
  fc := TFormClass(GetClass(AParameter));
  Application.CreateForm(fc, frm);
end;

procedure TApplication.GoBack;
begin
  window.history.back();
end;

procedure TApplication.AutoCreateForm(AInstanceClass: TFormClass; var AReference);
begin
  if Assigned(OnAutoCreateForm) then
    OnAutoCreateForm(Self, AInstanceClass, AReference)
  else
    CreateForm(AInstanceClass, AReference);
end;

procedure TApplication.CreateForm(AInstanceClass: TFormClass; var AReference);
begin
  CreateForm(AInstanceClass, FAppContainer, AReference, nil);
end;

procedure TApplication.CreateForm(AInstanceClass: TDataModuleClass; var AReference);
var
  lModule: TDataModule;
begin
  lModule := AInstanceClass.Create(Self);
  AReference := lModule;
end;

procedure TApplication.CreateForm(AInstanceClass: TFrameClass; var AReference);
const
  cHTMLCode = '<html><head><meta http-equiv="Content-type" content="text/html; ' +
    'charset=utf-8" /><title>TMS Web Project</title><style></style></head>' +
    '<body></body></html>';
var
  lFrame: TFrame;
  lForm: TFramePreviewForm;
begin
  lForm := TFramePreviewForm.Create(Application);
  lFrame := AInstanceClass.Create(lForm);
  lForm.FFrame := lFrame;
  lForm.Popup := False;
  AReference := lFrame;

  Application.FActiveForm := lForm;
  Application.CreateNewForm(lForm, cHTMLCode);
end;

procedure TApplication.CreateNewForm(AForm: TCustomForm; HTML: string);
var
  eh,op,span,childspan,formspan: TJSHTMLElement;
  l,t,w,h: integer;
  LCreatedProc: TFormCreatedProc;
  LCaptionDiv: TJSHTMLElement;
  LCaptionHeight: integer;
  ro: TJSOBject;
begin
  if FMainForm = nil then
    FMainForm := AForm;

  span := nil;
  formspan := nil;
  LCaptionHeight := 0;

  // form hosted in designated container
  if not AForm.Popup and (AForm.FFormElement <> '') then
  begin
    AForm.FLayer := nil;
    eh := TJSHTMLElement(document.getElementById(AForm.FFormElement));
    if Assigned(eh) then
    begin
      eh.innerHTML := HTML;
      if (AForm.ElementClassname = '') then
        eh.style.setProperty(CSSBackground,ColorToHTML(AForm.Color));
    end;
  end
  else
  begin
    AForm.ClearControls;
    // transparent mouse blocking layer
    AForm.FLayer := AForm.CreateLayer;
    document.body.appendChild(AForm.FLayer);

    eh := TJSHTMLElement(AForm.FLayer);

    // insert opacity layer
    if AForm.Popup and (AForm.PopupOpacity < 1) then
    begin
      op := AForm.CreateLayer;
      op.style.setProperty(CSSBackground, 'black');
      op.style.setProperty('opacity', DoubleToHTML(AForm.PopupOpacity));
      op.style.setProperty(CSSZIndex, Application.MaxZIndexStr);
      eh.appendChild(op);
    end;

    Application.ChangeMaxZIndex(+1);

    span := TJSHTMLElement(document.createElement('DIV'));
    // highest z-index but below modal dialog z-index
    span.style.setProperty(CSSZIndex, Application.MaxZIndexStr);
    AForm.FPopupElement := span;

    if (AForm.Border = fbDialogSizeable) then
    begin
      span.style.setProperty('resize','both');
      span.style.setProperty('overflow','auto');

      ro := nil;
      asm
        try
        {
        var frm = AForm;
        ro = new ResizeObserver( function(entries)  {
           window.requestAnimationFrame(() => {
              if (!Array.isArray(entries) || !entries.length) {
                return;
              }
           entries.forEach( function(entry) {
             frm.HandleObserver();
             });
             } );
        });
          // Observe the scrollingElement for when the window gets resized
          ro.observe(span);
        }
        catch(err)
        {
          span.style.removeProperty('resize');
          span.style.removeProperty('overflow');
        }
      end;

      AForm.FResizeObserver := ro;
    end;

    if AForm.ElementClassName <> '' then
      span.setAttribute('class', AForm.ElementClassName);

    if AForm.Shadow and AForm.Popup then
      span.style.setProperty('box-shadow','3px 3px 3px silver');

    eh.appendChild(span);

    AForm.GetMethodPointers;

    if (AForm.Border in [fbDialog, fbDialogSizeable]) then
    begin
      LCaptionDiv := AForm.CreateCaption;
      span.appendChild(LCaptionDiv);
      childspan := TJSHTMLElement(document.createElement('DIV'));
      childspan.style.setProperty('position','absolute');
      childspan.style.setProperty('width','100%');
//      childspan.style.setProperty('height','100%');
      childspan['id']:= 'childspan';
      span.appendChild(childspan);
      formspan := childspan;
      LCaptionHeight := Application.FormCaptionHeight;
    end
    else
    begin
      formspan := span;
    end;

    formspan.innerHTML := HTML;
    AForm.Container := formspan;
  end;

  AForm.Init;
  AForm.LoadDFMValues;

  if (AForm.Border in [fbDialog, fbDialogSizeable]) and (AForm.ElementCaptionClassName <> '') then
  begin
    LCaptionDiv.ClassList.Add(AForm.ElementCaptionClassName);
    LCaptionDiv.style.removeProperty('background-color');
    LCaptionDiv.style.removeProperty('color');
  end;

  if Assigned(AForm.FLayer) then
  begin
    // ensure full screen background is white or adopts the form elementclassname when it is not popup
    if not AForm.Popup then
    begin
      if (AForm.ElementClassName = '') then
      begin
        if AForm.Color <> clNone then
          eh.style.setProperty(CSSBackground, ColorToHTML(AForm.Color))
        else
          eh.style.setProperty(CSSBackground, 'white')
      end
      else
      eh.setAttribute('class',AForm.ElementClassName);
    end;
  end;

  // position popup form centered
  if Assigned(span) and (AForm.FormContainer = '') and AForm.Popup then
  begin
    span.style.setProperty('position', 'absolute');

    l := max(0,Round((WinWidth - AForm.Width)/2)) + window.scrollX;
    t := max(0,Round((WinHeight - AForm.Height)/2)) + window.scrollY;

    w := AForm.Width;
    h := AForm.ClientHeight + LCaptionHeight;

    if (AForm.ElementClassName = '') then
    begin
      span.style.setProperty(CSSBackground,ColorToHTML(AForm.Color));
      span.style.setProperty('border','1px Black solid');
    end;

    span.style.setProperty('top',IntTostr(t)+'px');
    span.style.setProperty('left',IntToStr(l)+'px');
    span.style.setProperty('width',IntToStr(w)+'px');
    span.style.setProperty('height',IntToStr(h)+'px');
  end;

  if Assigned(formspan) then
    ActivateChildScripts(formspan);

  if Assigned(AForm.FCreatedProc) then
  begin
    LCreatedProc := AForm.FCreatedProc;
    AForm.FCreatedProc := nil;
    LCreatedProc(AForm);
  end;

  if Assigned(AForm.OnCreate) then
    AForm.OnCreate(AForm);

  AForm.Loaded;
  AForm.InitAnchoring;
  AForm.UpdateChildAnchoring;
  AForm.FCreating := false;
  AForm.UpdateElement;

  if not AForm.EventsBound then
    AForm.BindEvents;

  AForm.Resize;
  AForm.DoShow;
end;

function TApplication.DoFormLoad(Event: TEventListenerEvent): boolean;
var
  LResponse: string;
begin
  asm
    LResponse = Event.target.responseText;
  end;

  CreateNewForm(FLoadForm, LResponse);

  if Assigned(FCreatedProc) then
  begin
    FCreatedProc(Self);
    FCreatedProc := nil;
  end;

  Result := true;
end;

function TApplication.DoHandleExit(Event: TEventListenerEvent): boolean;
begin
  Result := true;
  if Assigned(OnExit) then
    OnExit(Self);
end;

function TApplication.DoHandleError(Event: TJSErrorEvent): boolean;
var
  err: TApplicationError;
  el,sp,x: TJSHTMLElement;
  msg,s: string;
  isError: boolean;
  jserror: JSValue;
  ex: Exception;
begin
  isError := true;

  asm
    if (Event.message == undefined) {
      isError = false;
    }
  end;

  // ignore this error
  if (Event.message = 'ResizeObserver loop limit exceeded') then
    Exit;

  // ignore this error
  if (Event.message = 'ResizeObserver loop completed with undelivered notifications.') then
    Exit;

  if not isError then
  begin
    err.AMessage := 'Undefined JavaScript exception';
    err.AFile := '';
    err.ALineNumber := 0;
    err.AColNumber := 0;
    err.AError := nil;

    if Assigned(OnError) then
      OnError(Self, err, Result);
    Exit;
  end;

  err.AMessage := Event.message;
  err.AFile := Event.filename;
  err.ALineNumber := Event.lineno;
  err.AColNumber := Event.colno;
  err.AError := Event.error;
  err.AStack := '';

  s := '';
  jserror := nil;
  // try to get stack & RTL exception message
  if Assigned(Event.error) then
  begin

    asm
      if (Event.error.stack) {
        err.AStack = Event.error.stack;
      }
      if (Event.error.fMessage) {
        err.AMessage = Event.error.fMessage;
      }
    end;

    asm
      function objToString (obj) {
      var str = '';
      for (var p in obj) {
          if (obj.hasOwnProperty(p)) {
              str += p + '::' + obj[p] + '\n';
          }
      }
      return str;
      }

      // detect if it was an RTL exception
      if (Event.error.hasOwnProperty('FJSError'))
      {
        jserror = Event.error['FJSError'];
      }


      s = objToString(Event.error);
    end;
  end;

  // it is an RTL exception, allow to handle by application object
  if Assigned(jserror) then
  begin
    asm
    ex = Event.error;
    end;
    if Assigned(OnException) then
      OnException(Self, ex);
  end;

  Result := false;

  msg := 'ERROR<br>' + err.AMessage + ' | ' + s + err.AStack + '<BR> at '+err.AFile + ' [' +inttostr(err.ALineNumber)+':'+inttostr(err.AColNumber)+']';

  case ErrorType of
  aeFooter:
    begin
      el := TJSHTMLElement(document.getElementById('tmserrormessage'));

      if Assigned(el) then
      begin
        (el.firstChild as TJSHTMLElement).innerHTML := msg;
      end
      else
      begin
        el := TJSHTMLElement(document.createElement('DIV'));
        el.setAttribute('id','tmserrormessage');
        el.style.setProperty('position','absolute');
        el.style.setProperty('font-family','Courier');
        el.style.setProperty('font-size','8pt');
        el.style.setProperty('bottom','0');
        el.style.setProperty('width','100%');
        el.style.setProperty('height','100px');
        el.style.setProperty('background','#ff0000');
        el.style.setProperty('color','#ffffff');
        TJSHTMLElement(document.body).style.setProperty('padding','0');
        TJSHTMLElement(document.body).style.setProperty('margin','0');
        sp := TJSHTMLElement(document.createElement('SPAN'));
        sp.style.setProperty('margin-left','4px');
        sp.style.setProperty('float','left');
        sp.style.setProperty('overflow','hidden');
        sp.style.setProperty('display','block');
        sp.innerHTML := msg;
        el.appendChild(sp);

        x := TJSHTMLElement(document.createElement('SPAN'));
        x.style.setProperty('font-family','Courier');
        x.style.setProperty('font-size','8pt');
        x.style.setProperty('font-weight','bold');
        x.style.setProperty('position','absolute');
        x.style.setProperty('width','20px');
        x.style.setProperty('vertical-align','top');
        x.style.setProperty('cursor','pointer');
        x.style.setProperty('text-align','right');
        x.style.setProperty('right','4px');
        x.style.setProperty('display','block');
        x.innerHTML := 'X';
        x.onClick := @DoErrorClose;
        el.appendChild(x);

        document.body.appendChild(el);
      end;
    end;
  aeAlert: window.alert(msg);
  aeDialog:
    begin
        AddControlLink('googlematerial', 'https://fonts.googleapis.com/icon?family=Material+Icons');
      MessageDlg(msg, mtError, [mbOK]);
    end;
  end;

  if Assigned(OnError) then
    OnError(Self, err, Result);
end;

function TApplication.GetBootstrapVersion: TBootstrapVersion;
var
  bv: string;
begin
  bv := '';

  asm
    if (typeof bootstrap !== 'undefined') {
      bv = bootstrap.Tooltip.VERSION;
    }
  end;

  Result := bv3;
  if pos('4.',bv) = 1 then
    Result := bv4
  else
  if pos('5.',bv) = 1 then
    Result := bv5;
end;

function TApplication.DoPopState(Event: TJSPopStateEvent): boolean;
begin
  if Assigned(OnPopState) then
    OnPopState(Self, Event.state);
  Result := true;
end;


function TApplication.DoHashChange(Event: TEventListenerEvent): boolean;
var
  s: string;
  fc: TFormClass;
  frm: TCustomForm;
  bdy: TJSHTMLElement;
  Handled: boolean;

begin
  Result := true;

  if not AutoFormRoute then
    Exit;

  asm
    s  = location.hash;
  end;

  // remove hash sign to get class name
  Delete(s,1,1);

  Handled := false;

  if Assigned(OnHashChange) then
    OnHashChange(Self, s, Handled);

  if Handled then
    Exit;

  if s = '' then
    s := FInitFormClassName;

  if s <> '' then
  begin
    if FInitFormClassName = '' then
      FInitFormClassName := FActiveForm.ClassName;

    fc := TFormClass(GetClass(s));

    if Assigned(fc) then
    begin
      bdy := TJSHTMLElement(document.body);
      CreateForm(fc, bdy, frm);
    end;
  end;
end;

function TApplication.DoUpdateOnlineStatus(Event: TEventListenerEvent): boolean;
begin
  Result := true;

  if Assigned(OnOnlineChange) then
  begin
    if window.navigator.onLine then
      OnOnlineChange(Self, osOnline)
    else
      OnOnlineChange(Self, osOffline);
  end;
end;

function TApplication.DoVisibilityChange(Event: TEventListenerEvent): boolean;
var
  res: boolean;
begin
  Result := true;

  res :=  (document.visibilityState = 'visible');

  if Assigned(OnActivate) then
    OnActivate(Self, res);
end;

procedure TApplication.Download(const AURL: string);
begin
  asm
    window.location.href = AURL;
  end;
end;

procedure TApplication.DownloadTextFile(const AText: string; AFileName: string = '');
begin
  asm
    var element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(AText));
    if (AFileName != ''){
      element.setAttribute('download', AFileName);
    }
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  end;
end;

function TApplication.EXEName: string;
begin
  Result := window.document.location.href;
end;

function TApplication.GetActive: boolean;
begin
  Result := not document.hidden;
end;

function TApplication.GetColorScheme: TApplicationColorScheme;
begin
  Result := csNoScheme;

  if (window.matchMedia('(prefers-color-scheme)').media <> 'not all') then
  begin

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) then
      Result := csDark
    else
      Result := csLight;
  end;
end;

function TApplication.IDETheme: string;
var
  s: string;
begin
  Result := '';

  if not Assigned(VSIDE) then
    Exit;

  asm
    s = pas["WEBLib.Forms"].VSIDE.theme;
  end;

  Result := s;
end;

procedure TApplication.IDEOpenURL(const URL: string);
begin
  asm
    pas["WEBLib.Forms"].VSIDE.Open(URL);
  end;
end;

procedure TApplication.IDEMessage(const Msg: string);
begin
  asm
    pas["WEBLib.Forms"].VSIDE.ShowMessage(Msg);
  end;
end;

function TApplication.IDECSS: string;
begin
  Result := '';
  if IDETheme = 'vscode-light' then
  begin
    Result := '.IDECaption { background-color: #F0F0F0 !important; color: #555555 !important; border: 1px solid #DDDDDD !important}'+#13#10+
              '.IDEBkg { background-color: white !important; color: #555555 !important; border: 1px solid #DDDDDD !important}'+#13#10+
              '.IDEButton { background-color: #FAFAFA !important; font-size:10pt; color: #555555 !important; border: 1px solid #DDDDDD !important}'+#13#10+
              '.IDEButton:hover {background-color:#F0F0F0 !important}'+#13#10+
              '.IDEFont {font-size:10pt !important}';
  end;

  if IDETheme = 'vscode' then
  begin
    Result := '.IDECaption,.IDEButton { background-color: #3F3E43 !important; color: #FFFFFF !important; border: 1px solid #35353A !important}'+#13#10+
              '.IDEBkg { background-color: #2A2A2C !important; color: #A9ACB4 !important; border: 1px solid #35353A !important}'+#13#10+
              '.IDEButton:hover {background-color:#434857 !important}'+#13#10+
              '.IDEFont {font-size:10pt !important}';
  end;
end;

procedure TApplication.InternalDownloadFile(const AData: TJSUint8Array; const AFileName, AType: string; const ANewTab: Boolean);
begin
  asm
    var lElement = document.createElement('a');
    var lBlob = new Blob([AData], {type: AType})
    var lUrl = window.URL.createObjectURL(lBlob);
    lElement.href = lUrl;
    if (AFileName != ''){
      lElement.setAttribute('download', AFileName);
    }
    lElement.style.display = 'none';
    if (ANewTab == true) {
      lElement.target = '_blank';
    }
    document.body.appendChild(lElement);
    lElement.click();
    document.body.removeChild(lElement);
  end;
end;

procedure TApplication.DownloadBinaryFile(const AData: TJSBlob; const AFileName: string = ''; const ANewTab: Boolean = False);
begin
  asm
    var lElement = document.createElement('a');
    var lUrl = window.URL.createObjectURL(AData);
    lElement.href = lUrl;

    if (AFileName != ''){
      lElement.setAttribute('download', AFileName);
    }

    lElement.style.display = 'none';
    if (ANewTab == true) {
      lElement.target = '_blank';
    }
    document.body.appendChild(lElement);
    lElement.click();
    document.body.removeChild(lElement);
  end;
end;

procedure TApplication.DownloadBinaryFile(const AData: TBytes; const AFileName: string; const ANewTab: Boolean);
begin
  DownloadBinaryFile(TJSUint8Array.from(AData), AFileName, ANewTab);
end;

procedure TApplication.DownloadBinaryFile(const AData: TJSUint8Array; const AFileName: string; const ANewTab: Boolean);
begin
  InternalDownloadFile(AData, AFileName, 'octet/stream', ANewTab);
end;

procedure TApplication.DownloadPDFFile(const AData: TBytes; const AFileName: string; const ANewTab: Boolean);
begin
  DownloadPDFFile(TJSUint8Array.from(AData), AFileName, ANewTab);
end;

procedure TApplication.DownloadPDFFile(const AData: TJSUint8Array; const AFileName: string; const ANewTab: Boolean);
begin
  InternalDownloadFile(AData, AFileName, 'application/pdf', ANewTab);
end;

function TApplication.GetAuthorizationPageHTML(const AAuthorizationSuccess: Boolean): string;
const
  sExternalBrowserAuthorizationOK = '<div class="text2">Application succesfully authorized</div>You can close this browser window';
  sExternalBrowserAuthorizationFailed = '<div class="text2">Application authorization failed</div><br/>Please try again.';
  LB = #13#10;

  ImageFail = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeA'+
  'AAACXBIWXMAAA7DAAAOwwHHb6hkAAABtElEQVR4nO2bUY7CMAxEhz0NB+EgrDjXCg6yB+E4+9NKCAiN'+
  '7XHcjf1+ED+Z6ahNHScFiqIoBNwvp+v9crpG+3hG6+sgFQFwXv7ejj+/31JBDyy+ugN4ElkJD8Hqqyu'+
  'AhohYjA3D12YAGyIiMSYsXx8D6BTpFmPB9NUMQCjSJcaA7ettAEqRTTErHr5eAjCKfBSz4OXryzhgiz'+
  'OzWCJd/Fs8HoFHzHeCtxf2JNgt3MMID8zXoMpAtDarEDIbidJklMJUQ6MDty6GNDSNRdxtluWwhReDU'+
  'fONKADAx2jkZCsOAOAaXn4pY2let6oAAN/qTIG61lAHAOwmBFO1aQoACA/BXGqbAwDCQqCsOCkBAMND'+
  'oC23aQEAw0Kg9hqoAQDuIdAbLfQAALcQXFptLgEA9BDc+oxeLbF/Qz0C7AFTT4KpX4OpC6HUpXDqxVD'+
  'q5fBOLn5lbEMkdUssdVM0dVs89cZI6q2x1JujqbfHUx+Q2MPFj/LidUqsKajB05NXS4y6Zl/GurHGe6'+
  'QOSpLF5jgqqxSb67C0UGzO4/KdYnN/MLEhluOTmYZY+MWvDPU122dzRVHk5g+X6Lw5aVkK9AAAAABJRU5ErkJggg==';

  ImageSuccess = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaX'+
  'HeAAAACXBIWXMAAA7DAAAOwwHHb6hkAAACC0lEQVR4nO2Zv3GDMBSHv+QyQEbICM4EOW+Q9BS4o2QTU'+
  '9LZBQNkg6zgETyCR0iRkOMIAv3Xk0+/zsiI931nI3iCkpKSkpKSkpKSkpJ7SDN0x2bo3nW//xCymNhp'+
  'hu4E1MAN2PdVe9k6524ETODHXIHXvmpva+c9BqwpWhbgAV6Ar61zsxeggB+z+x1XJmsBG/Bj6mboWtV'+
  'gtvcATfhpPvqq/ZwfzFKABTwoVobsBFjCj7kyWxmyugc4wsPCypCNAA/wY/L7C3iEP/dVe5geEC8gJD'+
  'wIFxAaHgQLiAEPQgXEggeBAmLCgzABseFBkIAU8CBEQCp4ECAgJTwYChibCzYXWpmv9jCVFTwYCJgVa'+
  '31BxXwucapFS4CiWOsLS4EHDQEbxRoXIAkeNgRoFqtdiDR4WBFgWOxmQRLhQdEQsSi2bobuqBqUCg8L'+
  'vwDHYg991Z49zjeNd3iYCfBU7J8E6fAAT7PPOw9znpqhA3hDODz8F7Dnp2vqKmJ1O8ogQeFh+R7wjB8'+
  'JrgkOD4plUICEKPCw/hyQSkI0eNh+EowtISo86L0LxJIQHR703wZDS0gCD2b9gFASksGDeUfIt4Sk8G'+
  'DRE/QoITk8WDZFPUgQAQ8OXWEHCWLgwbEtbiFBFDx42BcwkCAOHjxtjGhIEAkPHneGViSIhQfPW2MLE'+
  'kTDQ4C9wYmEi3T4kpKSkm+5ax+YhPsUTQAAAABJRU5ErkJggg==';

  PlaceHolderImage = '#PLACEHOLDERIMAGE#';
  PlaceHolderText = '#PLACEHOLDERTEXT#';
  PlaceHolderColor = '#PLACEHOLDERCOLOR#';

  AuthHTMLTemplate =
  '<!doctype html>'+LB+
  '<html lang="en">'+LB+
  '<head>'+LB+
  '  <title>Authentication Result</title>'+LB+
  '  <meta name="viewport" content="width=device-width, initial-scale=1">'+LB+
  '</head>'+LB+
  '<body>'+LB+
  '<style>'+LB+
  '  hr'+LB+
  '  {'+LB+
  '    border: none;'+LB+
  '    height: 1px;'+LB+
  '    background-color: rgb(171, 171, 171);'+LB+
  '  }'+LB+
  '  div.container'+LB+
  '  {'+LB+
  '    position: fixed;'+LB+
  '    font-family: Arial;'+LB+
  '    font-size: 12pt;'+LB+
  '    max-width: 100%;'+LB+
  '    max-height: 100%;'+LB+
  '    top: 50%;'+LB+
  '    left: 50%;'+LB+
  '    transform: translate(-50%, -50%);'+LB+
  '  }'+LB+
  '  div.section'+LB+
  '  {'+LB+
  '    display: inline-block;'+LB+
  '    margin: 15px;'+LB+
  '    padding: 5px;'+LB+
  '    float: left;'+LB+
  '  }'+LB+
  '  div.image img'+LB+
  '  {'+LB+
  '    height: 100%;'+LB+
  '  }'+LB+
  '  div.text'+LB+
  '  {'+LB+
  '    padding-top: 15px;'+LB+
  '  }'+LB+
  '  div.text2'+LB+
  '  {'+LB+
  '    font-size: 18pt;'+LB+
  '    color: #PLACEHOLDERCOLOR#;'+LB+
  '  }'+LB+
  '  span.title'+LB+
  '  {'+LB+
  '    font-size: 26px;'+LB+
  '  }'+LB+
  '</style>'+LB+
  '  <div class="container">'+LB+
  '  <div class="section image">'+LB+
  '    <img src="#PLACEHOLDERIMAGE#">'+LB+
  '  </div>'+LB+
  '  <div class="section text">'+LB+
  '  #PLACEHOLDERTEXT#'+LB+
  '  </div>'+LB+
  '  </div>'+LB+
  ' </body>'+LB+
  '</html>';

  function ReplacePlaceHolders(APlaceHolders: array of string; APlaceHolderValues: array of string): string;
  var
    I: Integer;
    s: string;
  begin
    s := AuthHTMLTemplate;
    for I := 0 to Length(APlaceHolders) - 1 do
      s := StringReplace(s, APlaceHolders[I], APlaceHolderValues[I], [rfReplaceAll]);

    Result := s;
  end;

begin
  if AAuthorizationSuccess then
    Result := ReplacePlaceHolders([PlaceHolderImage, PlaceHolderText, PlaceHolderColor], [ImageSuccess, sExternalBrowserAuthorizationOK, 'rgb(104, 164, 144)'])
  else
    Result := ReplacePlaceHolders([PlaceHolderImage, PlaceHolderText, PlaceHolderColor], [ImageFail, sExternalBrowserAuthorizationFailed, 'rgb(216, 99, 68)']);
end;

function TApplication.GetFormExtension: string;
begin
  if Language <> lNone then
    Result := '_' + GetLanguageISO639_1Code(Language) + cHTMLExt
  else
    Result := cHTMLExt;
end;

function TApplication.GetIsOnline: boolean;
begin
  Result := window.navigator.onLine;
end;

function TApplication.GetIsPWA: boolean;
var
  pwa: boolean;
begin
  asm
    pwa = ('standalone' in window.navigator) && (window.navigator.standalone);
  end;

  Result := pwa;
end;

{$HINTS OFF}
function TApplication.IsPWAInstalled: TJSPromise;
var
  b: boolean;
  s: string;
begin
  b := false;
  s := window.location.hostname;

  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver) async
    begin
      asm
        if ('getInstalledRelatedApps' in window.navigator) {
         const relatedApps = await navigator.getInstalledRelatedApps();
         relatedApps.forEach((app) => {
            if (app.id.indexOf(s) >= 0)
            {
              b = true;
            }
         });
        }
      end;
      ASuccess(b);
    end);
end;
{$HINTS ON}

function TApplication.GetIsMobile: boolean;
var
  mob: boolean;
begin
  asm
    function TestMobile() {
      var check = false;
     (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
     return check;
     };

    mob = TestMobile();
  end;

  Result := mob;
end;

{$IFDEF SINGLEINSTANCE}
procedure TApplication.DoGetDataReceived(Sender: TObject; Origin: string;
  Data: TJSObjectRecord);
begin
  if Data.jsobject.toString = '#reload' then
  begin
    asm
      location.reload();
    end;
  end;
end;

procedure TApplication.DoGetConnected(Sender: TObject);
var
  url,agent:string;
begin
  url := window.location.href;
  agent := window.navigator.useragent;

  if Pos('Edg', agent) > 0 then
    agent := 'Edge'
  else if Pos('Opera', agent) > 0 then
    agent := 'Opera'
  else if Pos('OPR', agent) > 0 then
    agent := 'Opera'
  else if Pos('Chrome', agent) > 0 then
    agent := 'Chrome'
  else if Pos('Safari', agent) > 0 then
    agent := 'Safari'
  else if Pos('Firefox', agent) > 0 then
    agent := 'Firefox'
  else if Pos('Trident', agent) > 0 then
    agent := 'MSIE'
  else
    agent := '';

  FWebSocket.Send(url + ';' + agent);
end;
{$ENDIF}

function TApplication.DoErrorClose(Event: TJSMouseEvent): boolean;
begin
  document.body.removeChild(Event.targetElement.parentElement);
  Result := true;
end;

function TApplication.DoFormAbort(Event: TEventListenerEvent): boolean;
begin
  ShowMessage('Failed to load form HTML template file');
  Result := true;
end;

procedure TApplication.TriggerNotification;
begin
  {$IFDEF FREEWARE}
  if FNotifyCount = 0 then
  begin
    {$IFDEF TMS}
    asm
      alert('TMS WEB Core \u00A9 2018 - 2023 tmssoftware.com - Trial Version');
    end;
    {$ENDIF}
    {$IFNDEF TMS}
    asm
      alert('Application created with an unlicensed trial version of software');
    end;
    {$ENDIF}
  end;
  inc(FNotifyCount);
  {$ENDIF}
end;

procedure TApplication.InitFormatSettings(const BrowserLocale: string);
var
  timestr: string;
  i: integer;
  locale: string;
begin
  locale := BrowserLocale;

  FormatSettings.ShortDateFormat := GetLocaleShortDateFormat(locale);

  if (Pos('/',FormatSettings.ShortDateFormat) > 0) then
    FormatSettings.DateSeparator := '/';

  if (Pos('.',FormatSettings.ShortDateFormat) > 0) then
    FormatSettings.DateSeparator := '.';

  if (Pos('-',FormatSettings.ShortDateFormat) > 0) then
    FormatSettings.DateSeparator := '-';

  asm
    var event = new Date('Jan 1, 1980 06:07:08 GMT+00:00');
    var loc = "";
    if (locale == "") { loc = navigator.language; } else
    { loc = locale; }
    timestr = event.toLocaleTimeString(loc);
  end;

  if pos(':',timestr) > 0 then
    FormatSettings.TimeSeparator := ':';

  if pos('.',timestr) > 0 then
    FormatSettings.TimeSeparator := '.';

  if pos('-',timestr) > 0 then
    FormatSettings.TimeSeparator := '-';

  for i := 1 to 7 do
  begin
    FormatSettings.ShortDayNames[i] := GetLocaleShortDayName(i,locale);
    FormatSettings.LongDayNames[i] := GetLocaleLongDayName(i,locale);
  end;

  for i := 1 to 12 do
  begin
    FormatSettings.ShortMonthNames[i] := GetLocaleShortMonthName(i,locale);
    FormatSettings.LongMonthNames[i] := GetLocaleLongMonthName(i,locale);
  end;

  FormatSettings.DecimalSeparator := GetLocaleDecimalSeparator;

  if FormatSettings.DecimalSeparator = '.' then
    FormatSettings.ThousandSeparator := ','
  else
    FormatSettings.ThousandSeparator := '.';
end;

{$HINTS OFF}

function TApplication.Route: boolean;
var
  frm: TCustomForm;
  hash: string;
  pc: TPersistentClass;
begin
  Result := false;
  FRouting := true;
  hash := window.location.hash;

  if (hash <> '') then
  begin
    delete(hash,1,1);
    hash := 'T' + hash;
    pc := GetClass(hash);
    if Assigned(pc) then
    begin
      Result := true;
      Application.AutoCreateForm(TFormClass(pc), frm);
    end;
  end;
end;

procedure TApplication.Initialize;
var
  query, token: string;
  p: Integer;
  b: Boolean;
begin

  FCanCreateForm := True;
  FAppInitializing := True;
  FParameters.Clear;
  query := window.location.href;

  InitFormatSettings(GetBrowserLocale);

  FParameters.Delimiter := '&';
  FParameters.StrictDelimiter := true;
  p := Pos('?', query);
  if p > 0 then
    query := Copy(query, p + 1, Length(query));
  FParameters.DelimitedText := query;

  asm
    b = (window.name == "Authentication");
  end;

  if Assigned(OnOAuthCallBack) then
  begin
    if (FParameters.IndexOfName('oauthcallback') <> -1) or b then
    begin
      OnOauthCallBack(Self, query);

      FIsRedirect := true;
      document.body.innerHTML := GetAuthorizationPageHTML(True);
      Exit;
    end;
  end;

  // there is a code callback
  if (FParameters.IndexOfName('code') <> -1) and HandleOAuth then
  begin
    if Assigned(OnOAuthToken) then
      OnOauthToken(Self, query);

    FIsRedirect := true;
    token := FParameters.Values['code'];
    asm
      if (window.opener && window.opener.processAuthData){
        window.opener.processAuthData(token);
        window.close();
      }
    end;
  end;

  window.addEventListener('hashchange',@DoHashChange);
  window.addEventListener('online',  @DoUpdateOnlineStatus);
  window.addEventListener('offline', @DoUpdateOnlineStatus);
  window.addEventListener('popstate', @DoPopState);
  document.addEventListener('visibilitychange', @DoVisibilityChange);

  if (window.opener <> nil) then
    if (FParameters.IndexOfName('access_token') <> -1) then
    begin
      FIsRedirect := true;
      token := FParameters.Values['access_token'];
      asm
        if (window.opener && window.opener.processAuthData){
          window.opener.processAuthData(token);
        	window.close();
        }
      end;
    end;
end;

procedure TApplication.InsertCSS(id, href: string);
var
  styleElement: TJSElement;
begin

  styleElement := document.getElementById(id);
  if Assigned(styleElement) then
  begin
    asm
      styleElement.href = href;
    end;
    Exit;
  end;

  styleElement := document.createElement('link');
  styleElement.id := id;

  asm
    styleElement.rel = 'stylesheet';
    styleElement.href = href;
  end;

  document.getElementsByTagName('head')[0].appendChild(styleElement);
end;

{$HINTS ON}

procedure TApplication.Run;
begin
  FCanCreateForm := True;
  FAppInitializing := False;
end;

procedure TApplication.RunScript(Source: string);
begin
  asm
    eval(Source);
  end;
end;

procedure TApplication.Terminate;
begin
  window.location.href := 'about:blank';
end;

procedure TApplication.ReceiveMessageFromClient(const AMessage: string);
begin
  if Assigned(FClientConnector) then
    FClientConnector.Receive(AMessage);
end;

procedure TApplication.ChangeLanguage(const Value: TUILanguage);
begin
  FLanguage := Value;
  if Assigned(FMainForm) then
    ReloadForm;
end;

procedure TApplication.SetLanguage(const Value: TUILanguage);
begin
  FLanguage := Value;
end;

procedure TApplication.ChangeCSS(id, href: string);
begin
  asm
    var styleElement = document.getElementById(id);
    if (styleElement) {
      styleElement.href = href;
      return;
    }
  end;
end;

function TApplication.MaxZIndexStr: string;
begin
  Result := IntToStr(FMaxZIndex);
end;

procedure TApplication.ChangeMaxZIndex(Delta: integer);
begin
  FMaxZIndex := FMaxZIndex + Delta;
end;

constructor TApplication.Create(AOwner: TComponent);
{$IFDEF HOTRELOAD}
const
  cDefPollInterval = 1000;
  cDefFileName = 'TMSHotreloadVersion.json';
  {$ENDIF}
begin
  FFormStack := TList.Create;
  FParameters := TStringList.Create;
  FAppContainer := cBodyTag;
  FMainForm := nil;
  FActiveForm := nil;
  FInitFormClassName := '';
  FIsRedirect := false;
  FAutoFormRoute := false;
  FThemed := false;
  FThemeColor := clTMSBlue;
  FThemeTextColor := clWhite;
  FFormCaptionHeight := 22;
  FMaxZIndex := 999998;
  FHandleOAuth := true;
  FHintPause := 200;
  FHintHidePause := 250;
  FHintPosition := hpTop;
  FHintClassName := '';
  {$IFDEF FREEWARE}
  FNotifyCount := 0;
  {$ENDIF}
  {$IFDEF HOTRELOAD}
  FHotReloadPollInterval := cDefPollInterval;
  FHotReloadFile := cDefFileName;
  StartHotReload;
  {$ENDIF}
  {$IFDEF SINGLEINSTANCE}
  FWebSocket := TSocketClient.Create(Self);
  FWebSocket.Port := DefaultPort;
  FWebSocket.OnDataReceived := @DoGetDataReceived;
  FWebSocket.OnConnect := @DoGetConnected;
  FWebSocket.Connect;
  {$ENDIF}

  window.addEventListener('error', @DoHandleError);
  window.addEventListener('unhandledrejection', @DoHandleError);
  window.addEventListener('beforeunload', @DoHandleExit);

  FErrorType := aeSilent;
  {$IFDEF DEBUG}
  FErrorType := aeFooter;
  {$ENDIF}
  //TriggerNotification;
end;

function TApplication.GetHash: string;
var
  s: string;
begin
  s := window.location.hash;
  if (Length(s) > 0) and (s[1] = '#') then
    Delete(s,1,1);


  Result := s;
end;

procedure TApplication.SetHash(const AValue: string);
begin
  window.location.hash := AValue;
end;


destructor TApplication.Destroy;
begin
  {$IFDEF HOTRELOAD}
  StopHotReload;
  {$ENDIF}
  {$IFDEF SINGLEINSTANCE}
  FWebSocket.Free;
  {$ENDIF}
  FFormStack.Free;
  FParameters.Free;
  inherited Destroy;
end;

procedure TApplication.LockForm(AForm: TCustomForm);
var
  i: integer;
begin
  for i := 0 to AForm.ControlCount - 1 do
  begin
    AForm.Controls[i].DisableTab;
  end;
end;

procedure TApplication.UnLockForm(AForm: TCustomForm);
var
  i: integer;
begin
  for i := 0 to AForm.ControlCount - 1 do
  begin
    AForm.Controls[i].EnableTab;
  end;
end;

procedure TApplication.PushForm(AForm: TCustomForm);
var
  i: integer;
begin
  // persist all control settings in HTML to be saved
  for i := 0 to AForm.ControlCount - 1 do
  begin
    AForm.Controls[i].PersistInHTML;
  end;

  // save with form
  AForm.FormContent := document.body.innerHTML;

  for i := 0 to AForm.ControlCount - 1 do
  begin
    AForm.Controls[i].DisableTab;
  end;

  FFormStack.Add(AForm);
end;

function TApplication.PopForm: TCustomForm;
var
  i: integer;
  frm: string;
begin
  if (FFormStack.Count > 0) then
  begin
    Result := TCustomForm(FFormStack.Items[FFormStack.Count - 1]);
    frm := Result.FormContent;
    FFormStack.Delete(FFormStack.Count - 1);

    // restore document
    document.body.innerHTML := frm;

    Result.InitFromHTML;

    // rebind form events
    Result.BindEvents;

    // rehook controls
    for i := 0 to Result.ControlCount - 1 do
      Result.Controls[i].HookElement;

    for i := 0 to Result.ControlCount - 1 do
    begin
      Result.Controls[i].RecreateCanvas;
      Result.Controls[i].InternalResize;
    end;

    Application.FActiveForm := Result;
  end;
end;

function TApplication.Version: string;
begin
  Result := WEBCOREVERSION;
end;

{$IFDEF HOTRELOAD}
procedure TApplication.OnHotReloadTick;
const
  cMethodLoad = 'load';
  cMethodAbort = 'abort';
  cMethodGet = 'GET';
  cDeli = '/';
var
  lReq: TJSXMLHttpRequest;
  lBuffer: string;
begin
  if FHotReloadReq <> nil then
    Exit;

  lReq := TJSXMLHttpRequest.New;
  lReq.AddEventListener(cMethodLoad, @OnHotReloadLoad);
  lReq.AddEventListener(cMethodAbort, @OnHotReloadAbort);
  lBuffer := window.document.location.href;
  lBuffer := TPath.GetDirectoryName(lBuffer);
  lBuffer := lBuffer + cDeli + FHotReloadFile;
  lReq.Open(cMethodGet, lBuffer);
  lReq.setRequestHeader('Cache-Control', 'no-cache, no-store, must-revalidate');
  lReq.Send;
  FHotReloadReq := lReq;
end;

function TApplication.OnHotReloadAbort(AEvent: TEventListenerEvent): Boolean;
begin
  FHotReloadReq := nil;
  Result := False;
end;

function TApplication.OnHotReloadLoad(AEvent: TEventListenerEvent): Boolean;
var
  lData: TJSObject;
begin
  try
    lData := TJSJSON.parseObject(FHotReloadReq.responseText);
    HandleHotReload(lData);
  except
    console.error('Error parsing Hotreload JSON: '+ FHotReloadReq.responseText);
  end;
  FHotReloadReq := nil;
  Result := True;
end;

procedure TApplication.HandleHotReload(const AData: TJSObject);
const
  cAttributeVersion = 'version';
var
  lVersion: JSValue;
begin
  lVersion := AData[cAttributeVersion];
  if FHotReloadVersion = 0 then
  begin
    FHotReloadVersion := Integer(lVersion);
    Exit;
  end;

  if Integer(lVersion) > FHotReloadVersion then
  begin
    FHotReloadVersion := Integer(lVersion);
    window.location.reload(false);
  end;
end;

procedure TApplication.StartHotReload;
begin
  if FHotReloadTimer = 0 then
    FHotReloadTimer := Window.setInterval(@OnHotReloadTick, FHotReloadPollInterval);
end;

procedure TApplication.StopHotReload;
begin
  if FHotReloadTimer <> 0 then
  begin
    Window.ClearInterval(FHotReloadTimer);
    FHotReloadTimer := 0;
  end;
end;

{$ENDIF}

{ TCustomFrame }

constructor TCustomFrame.Create(AId: string);
begin
  inherited Create(AId);
  FPadding := TPadding.Create;
  LoadHTMLFile(AId);
end;

constructor TCustomFrame.Create(AOwner: TComponent);
begin
  inherited Create(AOwner);
  FPadding := TPadding.Create;
  FConstraints := TSizeConstraints.Create(Self);
end;

constructor TCustomFrame.CreateNew(AOwner: TComponent);
begin
  Create(AOwner);
  BeforeLoadDFMValues;
  try
    LoadFromForm;
  finally
    AfterLoadDFMValues;
  end;
end;

destructor TCustomFrame.Destroy;
begin
  FConstraints.Free;
  FPadding.Free;
  inherited Destroy;
end;

class function TCustomFrame.GetHTMLFileName: string;
begin
  Result := UnitName + Application.GetFormExtension;
end;

procedure TCustomFrame.LoadDFMValues;
begin
end;

procedure TCustomFrame.LoadFromForm;
begin
  LoadDFMValues;
end;

procedure TCustomFrame.LoadHTMLFile(const AId: string);
const
  cMethodGet = 'GET';
  cTypeLoad = 'load';
  {$IFDEF DEBUG}
  cHeaderCacheControl = 'Cache-Control';
  cValueNoCache = 'no-cache';
  {$ENDIF}
var
  lFileName: string;
  lRequest: TJSXMLHttpRequest;

  function DoStatusCreate(AEvent: TEventListenerEvent): Boolean;
  var
    lElement: TJSHTMLElement;
    lResponse: string;
  begin
    if AEvent = nil then
      Exit(False);

    asm
      lResponse = AEvent.target.responseText;
    end;
    lElement := TJSHTMLElement(document.getElementById(AId));
    lElement.innerHTML := lResponse;

    Result := True;
  end;

begin
  lFileName := GetHTMLFileName;
  if lFileName <> '' then
  begin
    lRequest := TJSXMLHttpRequest.new;
    lRequest.addEventListener(cTypeLoad, @DoStatusCreate);
    lRequest.open(cMethodGet, lFileName);
    {$IFDEF DEBUG}
    lRequest.setRequestHeader(cHeaderCacheControl, cValueNoCache);
    {$ENDIF}
    lRequest.send;
  end;
end;

procedure TCustomFrame.UpdateElement;
var
  nocolor: boolean;
begin
  inherited;

  nocolor := Color in [clBtnFace, clNone, clWhite, clWindow];

  if Assigned(ElementHandle) and not IsUpdating and (csDesigning in ComponentState) and nocolor then
  begin
    ElementHandle.style.setProperty('background-color',ColorToHTML($F0F0F0));
  end;
end;

procedure TCustomFrame.SetConstraints(const AValue: TSizeConstraints);
begin
  FConstraints.Assign(AValue);
end;

procedure TCustomFrame.SetPopupMenu(AValue: TPopupMenu);
begin
  FPopupMenu := AValue;
end;

procedure TCustomFrame.SetPadding(AValue: TPadding);
begin
  FPadding.Assign(AValue);
end;

{ TCustomSizeConstraints }

constructor TCustomSizeConstraints.Create(AControl: TControl);
begin
  inherited Create;
  FControl := AControl;
end;

procedure TCustomSizeConstraints.AssignTo(ADest: TPersistent);
begin
  if ADest is TCustomSizeConstraints then
  begin
    with TCustomSizeConstraints(ADest) do
    begin
      FMinHeight := Self.FMinHeight;
      FMaxHeight := Self.FMaxHeight;
      FMinWidth := Self.FMinWidth;
      FMaxWidth := Self.FMaxWidth;
      Change;
    end;
   end
  else
    inherited AssignTo(ADest);
end;

procedure TCustomSizeConstraints.SetConstraints(AIndex: Integer; AValue: TConstraintSize);
begin
  case AIndex of
    0:
      if AValue <> FMaxHeight then
      begin
        FMaxHeight := AValue;
//        if not FControl.FIScaling then
        begin
          if (AValue > 0) and (AValue < FMinHeight) then
            FMinHeight := AValue;
          Change;
        end;
      end;
    1:
      if AValue <> FMaxWidth then
      begin
        FMaxWidth := AValue;
//        if not FControl.FIScaling then
        begin
          if (AValue > 0) and (AValue < FMinWidth) then
            FMinWidth := AValue;
          Change;
        end;
      end;
    2:
      if AValue <> FMinHeight then
      begin
        FMinHeight := AValue;
//        if not FControl.FIScaling then
        begin
          if (FMaxHeight > 0) and (AValue > FMaxHeight) then
            FMaxHeight := AValue;
          Change;
        end;
      end;
    3:
      if AValue <> FMinWidth then
      begin
        FMinWidth := AValue;
 //       if not FControl.FIScaling then
        begin
          if (FMaxWidth > 0) and (AValue > FMaxWidth) then
            FMaxWidth := AValue;
          Change;
        end;
      end;
  end;
end;

procedure TCustomSizeConstraints.Change;
begin
  if Assigned(FOnChange) then
    FOnChange(Self);
end;

{ TDragDockObject }

constructor TDragDockObject.Create(AControl: TControl);
begin
  inherited Create;
  FBrush := TBrush.Create;
end;

destructor TDragDockObject.Destroy;
begin
  FBrush.Free;
  inherited Destroy;
end;

procedure TDragDockObject.Assign(Source: TDragObject);
begin
end;

procedure TFramePreviewForm.LoadDFMValues;
begin
  inherited LoadDFMValues;

  FFrame.SetParentComponent(Self);
  FFrame.LoadFromForm;
  FFrame.BeforeLoadDFMValues;
  try
    FFrame.Name := 'Frame1';
    FFrame.Left := 0;
    FFrame.Top := 0;
    FFrame.Width := 640;
    FFrame.Height := 480;
  finally
    FFrame.AfterLoadDFMValues;
  end;

  Name := 'Form1';
  Left := 0;
  Top := 0;
  Width := 640;
  Height := 480;
end;

function Sleep(milliseconds: cardinal): TJSPromise;
begin
  Result := TJSPromise.New(
  procedure(resolve,reject : TJSPromiseResolver)
  begin
    window.setTimeout(
    procedure()
    begin
      resolve(milliseconds);
    end,
    milliseconds);
  end);
end;


initialization
begin
  HandShakeScript := TJSHTMLScriptElement(document.createElement('script'));
  HandShakeScript.id := 'HandShakeScript';
  HandShakeScript.type_ := 'text/javascript';
  HandShakeScript.innerHTML :=
  'var IDE = null;'+#13#10+
  'var TMSWEBCoreClientIdentifier = "unknown";'+#13#10+
  'var TMSWEBCoreOAuthCallback = "unknown";'+#13#10+
  'function HandShake(cid){'+#13#10+
  '  TMSWEBCoreClientIdentifier = cid;'+#13#10+
  '}';
  document.body.appendChild(HandShakeScript);

  Application := TApplication.Create(nil);
end;

end.


